/**
 * MarketingBookForm · v2.7.0 batch DE
 * ====================================
 * Hero-embedded "Get a Fare In Seconds" widget with Square card hold.
 *
 * Flow:
 *   1. Rider fills name/phone/email + addresses + pax/ride-type
 *   2. Clicks QUOTE → backend returns fare estimate
 *   3. Quote panel opens with the AMOUNT + an inline Square card form
 *      replacing the old "Open Booking" green button
 *   4. Rider enters card → SDK tokenizes → backend authorizes the HOLD
 *      via /api/site/quick-booking-with-hold
 *   5. Trip lands in Admin → Book A Ride with payment_status=authorized
 *      and square_payment_id attached, pinging online drivers immediately
 *   6. After trip completes, dispatch captures the final amount from
 *      Admin → Edit Trip → Capture Hold button
 *
 * REQUEST DISPATCH (no-card) path is preserved for riders who'd rather
 * pay cash at pickup — they skip the Quote and submit name+phone only.
 *
 * Background asset support (v2.6.9 batch BU): pass `bgUrl` prop or set
 * site_config.home.quick_fare_background_url; the card backdrop renders
 * the image with a dark overlay so form contrast stays readable.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import SmsConsent from "@/components/marketing/SmsConsent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, MapPin, Users, Car, Calculator, Send, CheckCircle2,
  User, Phone, Mail, Clock, CalendarClock, ShieldCheck, CreditCard, X,
  Plus, Moon, PartyPopper, Info, UserCircle2, QrCode, Banknote,
  Ticket, MapPinned, Search, UserCheck, UserPlus, Pin, Star, RotateCcw, AlertTriangle, GitMerge, Flag, Check, Languages,
  PhoneIncoming, PhoneCall,
} from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CUSTOMER_TYPES } from "@/components/CustomerTypeBadge";
import api from "@/lib/api";
import adminApi from "@/lib/adminApi";
import { formatApiError } from "@/lib/errors";
import { fmtUsd } from "@/lib/format";
import { toast } from "sonner";
import SquareCardForm from "@/components/SquareCardForm";
import usePortalHours from "@/hooks/usePortalHours";
import ReopenCountdown from "@/components/ReopenCountdown";
import { RIDER_FLAGS, isCriticalFlag } from "@/lib/riderFlags";

const MAX_STOPS = 10;

// v2.7.0 batch DJ · Render hour+minute in "h:mma" form ("11:55pm", "5am").
// Drops the ":00" when minute is 0 so "5:00am" reads as "5am".
function formatHourMin(h, m) {
  const hh = ((h ?? 0) % 12) || 12;
  const mm = m || 0;
  const suf = (h ?? 0) < 12 ? "am" : "pm";
  return mm === 0 ? `${hh}${suf}` : `${hh}:${String(mm).padStart(2, "0")}${suf}`;
}

// v2.7.65+E229 · Dispatch-only phone mask → "(000) 000-0000". Strips to digits
// then re-inserts the parens/space/dash progressively as the agent types.
function formatUsPhone(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export default function MarketingBookForm({ bgUrl, dispatcherMode = false }) {
  // v2.7.65+E158 · Dispatcher mode — same form, same submit pipeline.
  // When `dispatcherMode` is true this component is mounted inside the
  // Admin Console. Behavior deltas: (1) tag every outbound booking with
  // `source: "admin_dispatch"` so the Ride Coordinator can badge the
  // row; (2) tighten the hero header to a dispatch label; (3) skip the
  // "install PWA" style hints that assume a marketing visitor.
  // Everything else — validation, quote, addresses, CCVAP voucher path,
  // stops, gender pref, Square hold — behaves identically.
  const BOOKING_SOURCE = dispatcherMode ? "admin_dispatch" : "public_quick_booking";
  // useNavigate retained for any future "confirm + see live tracking" link
  // (currently unused — kept so the import has a single source of truth and
  // we don't churn the file when we add the post-book live-track redirect).
  useNavigate();
  // v2.8+E313 · Live rider-portal Hours-of-Operation status. Gates the PUBLIC
  // quick-book widget to online-booking hours (dispatcher mode = 24/7, never
  // gated). Called unconditionally at the top before any early return.
  const portalHours = usePortalHours();
  const [name, setName] = useState("");  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  // v2.7.65+E159 · Rider Search-Ahead (dispatcher mode only) — as the
  // dispatch agent types a name or phone we hit /admin/riders/search
  // and offer 1-tap autofill so known callers don't have to be retyped.
  // Public homepage never receives these results (endpoint is admin-auth
  // gated) so the customer-facing form remains unchanged.
  const [riderSuggestions, setRiderSuggestions] = useState([]);
  const [showRiderSuggestions, setShowRiderSuggestions] = useState(false);
  // v2.7.65+E233 · which field the live-poll dropdown belongs under ('name'|'phone').
  const [suggestFor, setSuggestFor] = useState(null);
  // v2.7.65+E229 · Customer auto-lookup state (dispatcher mode only).
  const [lookupState, setLookupState] = useState(null);     // 'found' | 'new' | null
  const [pickupOptions, setPickupOptions] = useState([]);   // previously-used pickups + address on file
  const [dropoffOptions, setDropoffOptions] = useState([]); // previously-used drop-offs
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [showCustResults, setShowCustResults] = useState(false);
  // v2.7.65+E230 · Recent Callers (quick-rebook chips) + smart pickup default.
  const [recentCallers, setRecentCallers] = useState([]);
  const [mostUsedPickup, setMostUsedPickup] = useState("");
  // v2.7.65+E231 · Home-address capture, favorite-pickup pin, history peek.
  const [homeAddr, setHomeAddr] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [favoritePickup, setFavoritePickup] = useState("");
  const [historyByKey, setHistoryByKey] = useState({});
  // v2.7.65+E232 · Required Customer Type classification (dispatch only).
  const [customerType, setCustomerType] = useState("");
  // v2.7.65+E247 · Merge Duplicate Customers (dispatch lookup one-click).
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeConfirmPhone, setMergeConfirmPhone] = useState(null);
  useEffect(() => {
    if (!dispatcherMode) return undefined;
    // v2.7.65+E233 · Live customer poll. Name searches use the typed text; phone
    // searches use DIGITS ONLY so the "(580) 555-…" mask still matches stored
    // numbers. Backend floor is 2 chars. Short 120ms debounce ≈ real-time.
    const q = name.trim() ? name.trim() : phone.replace(/\D/g, "");
    if (q.length < 2) { setRiderSuggestions([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const token = window.localStorage.getItem("rtg_admin_token");
        if (!token) { setRiderSuggestions([]); return; }
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${backend}/api/admin/riders/search?q=${encodeURIComponent(q)}&limit=8`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        const riders = data?.riders || [];
        setRiderSuggestions(riders);
        setSuggestActiveIdx(-1);
        setLookupState(riders.length ? "found" : (q.length >= 3 ? "new" : null));
      } catch { /* silent — autofill is best-effort */ }
    }, 120);
    return () => clearTimeout(t);
  }, [dispatcherMode, name, phone]);

  // v2.7.65+E229 · Dedicated top-of-form "Customer Lookup" search bar (dispatch
  // only). Uses the same /admin/riders/search endpoint with its own query state
  // so it doesn't fight the inline name/phone search-ahead.
  useEffect(() => {
    if (!dispatcherMode) return undefined;
    const q = custQuery.trim();
    if (q.length < 2) { setCustResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const token = window.localStorage.getItem("rtg_admin_token");
        if (!token) return;
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${backend}/api/admin/riders/search?q=${encodeURIComponent(q)}&limit=8`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        const riders = data?.riders || [];
        setCustResults(riders);
        setLookupState(riders.length ? "found" : (q.length >= 3 ? "new" : null));
      } catch { /* silent */ }
    }, 250);
    return () => clearTimeout(t);
  }, [dispatcherMode, custQuery]);

  // v2.7.65+E230 · Recent Callers — last 5 distinct callers for 1-tap rebooking.
  useEffect(() => {
    if (!dispatcherMode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = window.localStorage.getItem("rtg_admin_token");
        if (!token) return;
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${backend}/api/admin/riders/recent?limit=5`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setRecentCallers(data?.riders || []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [dispatcherMode]);

  // v2.7.65+E229 · One-tap autofill for a chosen customer — name/phone/email +
  // pickup (smart default = most-used) + previously-used pickup & drop-off history.
  const pickRiderSuggestion = async (row) => {
    if (row?.name)  setName(row.name);
    if (row?.phone) setPhone(dispatcherMode ? formatUsPhone(row.phone) : row.phone);
    if (row?.email) setEmail(row.email);
    setShowRiderSuggestions(false);
    setShowCustResults(false);
    setLookupState("found");
    const home = (row?.home_address || "").trim();
    const fav = (row?.favorite_pickup || "").trim();
    setSelectedCustomerId(row?.id || null);
    setFavoritePickup(fav);
    setHomeAddr(home);
    if (row?.customer_type) setCustomerType(row.customer_type);
    if (home || fav) { setOrigin(fav || home); setPickupOptions([fav || home].filter(Boolean)); }
    setMostUsedPickup("");
    try {
      const token = window.localStorage.getItem("rtg_admin_token");
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const params = new URLSearchParams();
      if (row?.phone) params.set("phone", row.phone);
      if (row?.email) params.set("email", row.email);
      const r = await fetch(`${backend}/api/admin/riders/address-history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = r.ok ? await r.json() : { pickup_addresses: [], dropoff_addresses: [] };
      // Backend returns pickups ordered by frequency (most-used first) for the
      // chip list, plus explicit `recent_pickup`/`recent_dropoff` for the default.
      const hist = (data.pickup_addresses || []).map((a) => (a || "").trim()).filter(Boolean);
      const recentPickup = (data.recent_pickup || "").trim();
      const recentDropoff = (data.recent_dropoff || "").trim();
      const options = [];
      const addUnique = (v) => { if (v && !options.some((p) => p.toLowerCase() === v.toLowerCase())) options.push(v); };
      addUnique(fav);           // pinned "home" pickup always first
      addUnique(recentPickup);  // then the most-recent pickup
      hist.forEach(addUnique);  // then most-used → least-used
      addUnique(home);          // address on file as a fallback option
      const dropoffs = (data.dropoff_addresses || []).map((a) => (a || "").trim()).filter(Boolean);
      setPickupOptions(options);
      setDropoffOptions(dropoffs);
      setMostUsedPickup(hist[0] || "");
      // v2.7.65+E246 · Operator directive: default to the caller's MOST-RECENT
      // pickup + drop-off (pinned home still wins; chips allow any override).
      const defaultPickup = fav || recentPickup || hist[0] || home || "";
      if (defaultPickup) setOrigin(defaultPickup);
      const defaultDropoff = recentDropoff || dropoffs[0] || "";
      if (defaultDropoff) setDestination(defaultDropoff);
    } catch { /* best-effort */ }
  };

  // v2.7.65+E231 · Pin/unpin a caller's "home" pickup — always wins as default.
  const toggleFavoritePickup = async (addr) => {
    if (!selectedCustomerId) { toast.error("Pick a saved customer first"); return; }
    const isFav = favoritePickup && favoritePickup.toLowerCase() === (addr || "").toLowerCase();
    const newFav = isFav ? "" : (addr || "");
    setFavoritePickup(newFav);
    if (newFav) setOrigin(newFav);
    try {
      await adminApi.post("/admin/riders/favorite-pickup", { customer_id: selectedCustomerId, address: newFav });
      toast.success(newFav ? "Pinned as this caller's home pickup" : "Unpinned home pickup");
    } catch { toast.error("Could not save favorite pickup"); }
  };

  // v2.7.65+E231 · Lazy-load a recent caller's last-3 trips for the history peek.
  const fetchCallerHistory = async (caller, key) => {
    if (historyByKey[key]) return;
    try {
      const token = window.localStorage.getItem("rtg_admin_token");
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const params = new URLSearchParams();
      if (caller?.phone) params.set("phone", caller.phone);
      if (caller?.email) params.set("email", caller.email);
      params.set("limit", "3");
      const r = await fetch(`${backend}/api/admin/riders/trips?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = r.ok ? await r.json() : { trips: [] };
      setHistoryByKey((prev) => ({ ...prev, [key]: data.trips || [] }));
    } catch { setHistoryByKey((prev) => ({ ...prev, [key]: [] })); }
  };

  // v2.7.65+E247 · One-click merge of duplicate customer records sharing a
  // phone. Backend auto-picks the best survivor (most rides → most complete →
  // oldest) and re-points rides/bookings/cards to it. Dispatch-only.
  const mergeDuplicatesForPhone = async (phoneDigits) => {
    setMergeBusy(true);
    try {
      const { data } = await adminApi.post("/admin/customers/merge", { phone: phoneDigits });
      const n = data?.merged_count || 0;
      toast.success(`Merged ${n} duplicate${n === 1 ? "" : "s"} into one customer record`);
      setMergeConfirmPhone(null);
      // Refresh the lookup results so the extras disappear immediately.
      const q = (custQuery.trim() || phoneDigits);
      if (q.length >= 2) {
        const token = window.localStorage.getItem("rtg_admin_token");
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${backend}/api/admin/riders/search?q=${encodeURIComponent(q)}&limit=8`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) { const d = await r.json(); setCustResults(d?.riders || []); }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not merge duplicates — try again");
    } finally {
      setMergeBusy(false);
    }
  };

  // v2.7.65+E251 · Reset the dispatch form to a clean slate — no page reload,
  // no navigation, nothing else in the console touched. Confirms first if the
  // dispatcher has started filling anything in.
  const isFormDirty = () => Boolean(
    name || phone || email || origin || destination || (custQuery || "").trim() ||
    (dispatcherNotes || "").trim() || customerType || String(totalCost || "").trim() ||
    assignDriverId || (stops && stops.length) || rideType !== "single" || pax !== 1 ||
    dispatchPayment !== "cash" || roundTrip || bookAhead || isVoucherTrip ||
    scheduleMode !== "asap"
  );
  const resetForm = () => {
    setName(""); setPhone(""); setEmail("");
    setRiderSuggestions([]); setShowRiderSuggestions(false); setSuggestFor(null);
    setLookupState(null); setPickupOptions([]); setDropoffOptions([]);
    setCustQuery(""); setCustResults([]); setShowCustResults(false);
    setMostUsedPickup(""); setHomeAddr(""); setSelectedCustomerId(null);
    setFavoritePickup(""); setCustomerType(""); setMergeConfirmPhone(null);
    setOrigin(""); setDestination(""); setPax(1); setRideType("single");
    setPreferredDriverGender("any");
    setPrefersSms(true); setSmsConsent(false); setPrefersEmail(true);
    setScheduleMode("asap"); setScheduledDate(""); setScheduledTime("");
    setBookAhead(false); setBookAheadTouched(false);
    setStops([]); setGroupRiders("6"); setGroupQuote(null);
    setTotalCost(""); setPriceTouched(false);
    setBlurredFields({}); setSuggestActiveIdx(-1);
    setDispatchPayment("cash"); setDispatcherNotes(""); setRiderFlags([]);
    setLanguage(""); setLanguageOther("");
    setRideHistory([]); setRideHistoryLoading(false); setSelectedHistoryId("");
    setDispatchOrigin("call_in");
    setBannedInfo(null); bannedAddedForRef.current = "";
    setAssignDriverId(""); setAssignDriverName("");
    setRoundTrip(false); setReturnReverse(true); setReturnPickup(""); setReturnDropoff(""); setReturnDate(""); setReturnTime("");
    setIsVoucherTrip(false); setVoucherSerial(""); setVoucherStatus(null);
    setQuote(null); setConfirmation(null); setShowCardForm(false); setPayQrs(null);
    setDupWarning(null); setDupDismissedKey("");
    setWelcomeBack(null); setWelcomeDismissed(false);
    setResetConfirm(false);
    toast.success("Form cleared — ready for a new booking");
  };
  const handleResetClick = () => {
    if (resetConfirm) { resetForm(); return; }
    if (isFormDirty()) { setResetConfirm(true); return; }
    resetForm();
  };
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [pax, setPax] = useState(1);
  const [rideType, setRideType] = useState("single");
  // v2.7.7 · Gender-Preferred Dispatch — public booking safety preference.
  const [preferredDriverGender, setPreferredDriverGender] = useState("any");
  // v2.7.65+E91 · Per-booking notification override — checkboxes on the form.
  // When the rider has an existing profile these hydrate to the profile default
  // on returning-rider lookup (see /site/lookup-rider). Otherwise both default
  // to true. When either is unchecked, that channel gets skipped for THIS booking.
  const [prefersSms, setPrefersSms] = useState(true);
  // v2.7.65+E165 · TCPA-style SMS consent — unchecked by default, recorded
  // on submit. Legal-required disclosure is rendered by <SmsConsent/>.
  const [smsConsent, setSmsConsent] = useState(false);
  const [prefersEmail, setPrefersEmail] = useState(true);
  const [scheduleMode, setScheduleMode] = useState("asap"); // "asap" | "scheduled"
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  // v2.7.65+E215 · dispatcher-only Book-Ahead → route >=24h into Future Fares
  const [bookAhead, setBookAhead] = useState(false);
  // v2.7.65+E218 · Track whether the dispatcher manually toggled Book-Ahead.
  // Until they do, we auto-suggest it for 24h+ scheduled pickups.
  const [bookAheadTouched, setBookAheadTouched] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  // v2.7.0 batch DE · card-hold flow
  const [showCardForm, setShowCardForm] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);

  // v2.7.58+E40 · Storefront-wide Cash App + Venmo QR codes. Public GET —
  // no auth needed. Admin uploads the images in Admin → Payments →
  // Storefront Digital Wallet QRs; this component polls once on mount so
  // the images always reflect the current admin config.
  const [payQrs, setPayQrs] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/storefront/payment-qrs").then(({ data }) => {
      if (!cancelled) setPayQrs(data || null);
    }).catch(() => { /* silent — panel hides gracefully if endpoint fails */ });
    return () => { cancelled = true; };
  }, []);
  // v2.7.0 batch DH · multi-stop + dynamic surcharge banner
  // v2.7.12 · Stops carry stable `id`s so React keys are reorder-safe.
  // Shape: { id: string, value: string }[] — submit path strips to strings.
  const [stops, setStops] = useState([]); // array of {id, value}, up to MAX_STOPS
  const [surcharges, setSurcharges] = useState(null); // { late_night: {…}, holiday: {…} }

  // ── v2.8 · Admin "+Dispatch Booking" overhaul (dispatcher-mode only) ──
  // Every piece of state below is only rendered/sent when dispatcherMode is
  // true, so the public homepage Quick Book form is completely unaffected.
  const [dispDrivers, setDispDrivers] = useState([]);
  const [assignDriverId, setAssignDriverId] = useState("");
  const [assignDriverName, setAssignDriverName] = useState("");
  const [totalCost, setTotalCost] = useState("");
  // PROMPT 1 · Total Cost is ALWAYS manually editable by dispatch. The live
  // estimate only pre-fills it until the dispatcher touches the field; once
  // they type a fare (`priceTouched`) the estimate never overwrites it.
  const [priceTouched, setPriceTouched] = useState(false);
  // v2.7.65+E248 · Live Group-fare per-passenger breakdown (dispatch only).
  // Holds the /site/quote breakdown when Ride Type = Group so agents can quote
  // the per-passenger math on the spot. null when not a group ride.
  const [groupQuote, setGroupQuote] = useState(null);
  // v2.7.65+E250 · Total Riders in Group (dispatch-only). Shown when Ride Type
  // = Group; drives the group fare and is stored on the booking record.
  const [groupRiders, setGroupRiders] = useState("6");
  // v2.7.65+E251 · Reset Form (dispatch-only) — two-step confirm when dirty.
  const [resetConfirm, setResetConfirm] = useState(false);
  // v2.7.65+E234 · Inline required-field highlight — which required fields the
  // dispatcher has blurred (an empty blurred field turns red). Dispatch-only.
  const [blurredFields, setBlurredFields] = useState({});
  // v2.7.65+E234 · Keyboard nav index for the live Name/Phone suggestion list.
  const [suggestActiveIdx, setSuggestActiveIdx] = useState(-1);
  // v2.7.65+E235 · Booking Duplicate Guard (dispatch-only) — warns when the
  // same caller + route was booked in the last 10 minutes. Never blocks submit.
  const [dupWarning, setDupWarning] = useState(null);
  const [dupDismissedKey, setDupDismissedKey] = useState("");
  const [dispatchPayment, setDispatchPayment] = useState("cash"); // cash | card | already_processed
  const [dispatcherNotes, setDispatcherNotes] = useState("");
  // PROMPT 1 · Language flag — helps the driver know the rider's language.
  // "" (English) | "Spanish" | "Asian" | "Other"; `languageOther` = free text.
  const [language, setLanguage] = useState("");
  const [languageOther, setLanguageOther] = useState("");
  // PROMPT 2 · Customer ride history (dispatcher-only, read-only). Loaded when a
  // caller is identified by phone/email; selecting an entry shows full detail.
  const [rideHistory, setRideHistory] = useState([]);
  const [rideHistoryLoading, setRideHistoryLoading] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState("");
  // v2.8+E319 · Rider safety flags selected for this booking (dispatcher-only).
  const [riderFlags, setRiderFlags] = useState([]);
  // v2.8+E328 · Dispatch booking origin — "call_in" (rider phoned dispatch,
  // default) vs "self" (dispatch created the trip without a rider request).
  const [dispatchOrigin, setDispatchOrigin] = useState("call_in");
  // v2.8+E322 · Banned-rider auto-block. When the entered phone matches a rider
  // previously flagged "Banned Rider", warn dispatch (never hard-block) and
  // auto-add the flag to this booking.
  const [bannedInfo, setBannedInfo] = useState(null);
  const bannedAddedForRef = useRef("");
  useEffect(() => {
    if (!dispatcherMode) return undefined;
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { setBannedInfo(null); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await adminApi.get(`/admin/riders/banned-check?phone=${encodeURIComponent(phone)}`);
        if (cancelled) return;
        if (data?.banned) {
          setBannedInfo(data);
          if (bannedAddedForRef.current !== digits) {
            bannedAddedForRef.current = digits;
            setRiderFlags((prev) => prev.includes("Banned Rider") ? prev : [...prev, "Banned Rider"]);
          }
        } else {
          setBannedInfo(null);
        }
      } catch { if (!cancelled) setBannedInfo(null); }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [dispatcherMode, phone]);
  const [roundTrip, setRoundTrip] = useState(false);
  const [returnReverse, setReturnReverse] = useState(true);
  const [returnPickup, setReturnPickup] = useState("");
  const [returnDropoff, setReturnDropoff] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [returnTime, setReturnTime] = useState("");

  // Load the active-driver roster (same endpoint + logic the Ride
  // Coordinator uses) so dispatchers pick from a live list.
  // v2.8.1 · Bug fix — poll every 7s so the dropdown loads the moment the
  // form opens AND any driver who comes online afterwards appears WITHOUT a
  // page refresh (previously fetched once on mount and went stale).
  useEffect(() => {
    if (!dispatcherMode) return undefined;
    let cancelled = false;
    const fetchDrivers = async () => {
      try {
        const token = window.localStorage.getItem("rtg_admin_token");
        if (!token) return;
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${backend}/api/admin/queued-trips/active-drivers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setDispDrivers(data?.drivers || []);
      } catch { /* silent — dispatcher can still book, driver required at submit */ }
    };
    fetchDrivers();
    const poll = setInterval(fetchDrivers, 7000);
    return () => { cancelled = true; clearInterval(poll); };
  }, [dispatcherMode]);

  // v2.7.8 · Returning-rider greeting · debounced lookup whenever phone/email
  // mutates. We only call the backend once the phone reaches 9 digits or a
  // syntactically-valid email arrives, to avoid hammering the API on every
  // keystroke.
  const [welcomeBack, setWelcomeBack] = useState(null);  // { first_name, trip_count, last_pref } | null
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  // v2.7.65+E132 · Community Voucher Program integration.
  // When `isVoucherTrip` is Yes we:
  //   1) require a Voucher Serial Number
  //   2) force schedule_mode = "scheduled" with pickup between +24h and +72h
  //   3) enforce Weatherford geo-fence on both pickup + drop-off
  // The backend re-validates every rule server-side so a tampered payload
  // can never bypass these guards.
  const [isVoucherTrip, setIsVoucherTrip] = useState(false);
  const [voucherSerial, setVoucherSerial] = useState("");
  const [voucherValidating, setVoucherValidating] = useState(false);
  const [voucherStatus, setVoucherStatus] = useState(null);  // { ok, rider_first_name } | { error }

  // v2.7.65+E107 · Katie rider-bag prefill sync.
  // Mirror any name/phone/email the rider types into the Quick Booking
  // form into `localStorage.katie_rider_v1` so the Katie concierge
  // widget greets them by name on any subsequent page load. Writes only
  // when at least one field is non-empty so we don't stomp an existing
  // richer bag on empty renders.
  useEffect(() => {
    const first = (name || "").trim().split(/\s+/)[0] || "";
    if (!first && !phone && !email) return;
    try {
      const prev = JSON.parse(localStorage.getItem("katie_rider_v1") || "{}");
      const next = {
        ...prev,
        ...(first ? { first_name: first }              : {}),
        ...(phone ? { phone: phone.trim() }            : {}),
        ...(email ? { email: email.trim().toLowerCase() } : {}),
      };
      // Only write when something actually changed to avoid noisy events.
      if (JSON.stringify(next) !== JSON.stringify(prev)) {
        localStorage.setItem("katie_rider_v1", JSON.stringify(next));
        // Cross-tab hint: emit a StorageEvent-compatible signal so Katie
        // (mounted in the same tab) can pick up new identity mid-session.
        window.dispatchEvent(new StorageEvent("storage", { key: "katie_rider_v1", newValue: JSON.stringify(next) }));
      }
    } catch { /* private-mode · silent */ }
  }, [name, phone, email]);

  // v2.7.65+E235 · Booking Duplicate Guard (dispatch-only). Debounced check of
  // /admin/booking/duplicate-check when phone + pickup + drop-off are all set.
  useEffect(() => {
    if (!dispatcherMode) { setDupWarning(null); return undefined; }
    const digits = (phone || "").replace(/\D/g, "");
    const o = origin.trim();
    const d = destination.trim();
    if (digits.length < 10 || !o || !d) { setDupWarning(null); return undefined; }
    const key = `${digits}|${o.toLowerCase()}|${d.toLowerCase()}`;
    const t = setTimeout(async () => {
      try {
        const { data } = await adminApi.get("/admin/booking/duplicate-check", {
          params: { phone: digits, origin: o, destination: d, minutes: 10 },
        });
        const dup = data?.duplicate || null;
        setDupWarning(dup && key !== dupDismissedKey ? { ...dup, key } : null);
      } catch { /* silent — the guard is best-effort */ }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatcherMode, phone, origin, destination, dupDismissedKey]);

  // Compute the rider's pickup ISO once so the preview + quote agree.
  // v2.7.0 batch DH · Send the LOCAL datetime string (no `Z`) so the server
  // treats it as America/Chicago time per surcharge rules — converting to UTC
  // here would shift late-night windows by 5–6 hours depending on the rider's
  // browser TZ.
  const pickupIso = (() => {
    if (scheduleMode === "scheduled" && scheduledDate && scheduledTime) {
      return `${scheduledDate}T${scheduledTime}`;
    }
    return null;
  })();

  // Refresh surcharge preview whenever the rider's pickup time changes.
  useEffect(() => {
    let cancelled = false;
    const url = pickupIso ? `/site/surcharge-preview?at=${encodeURIComponent(pickupIso)}` : `/site/surcharge-preview`;
    api.get(url).then(({ data }) => { if (!cancelled) setSurcharges(data); }).catch(() => {});    return () => { cancelled = true; };
  }, [pickupIso]);

  // v2.7.65+E218 · Auto-suggest Book-Ahead → Future Fares for dispatchers when
  // the scheduled pickup is 24h+ out (matches the backend Future Fares rule),
  // so an advance booking is never accidentally dropped into the live queue.
  // The dispatcher can still uncheck it — once they touch it manually we
  // respect their choice and stop auto-toggling.
  useEffect(() => {
    if (!dispatcherMode || isVoucherTrip || bookAheadTouched) return;
    if (scheduleMode !== "scheduled" || !pickupIso) { setBookAhead(false); return; }
    const dt = new Date(pickupIso);
    if (Number.isNaN(dt.getTime())) return;
    setBookAhead((dt.getTime() - Date.now()) >= 24 * 60 * 60 * 1000);
  }, [dispatcherMode, isVoucherTrip, bookAheadTouched, scheduleMode, pickupIso]);


  // v2.8 · Auto-estimate the Total Cost from the public quote engine and
  // pre-fill it (dispatcher can override). Dispatcher-mode only; never stomps
  // a value the dispatcher has already typed.
  useEffect(() => {
    if (!dispatcherMode) return undefined;
    if (!origin || !destination) { setGroupQuote(null); return undefined; }
    const filledStops = stops.map((s) => (s.value || "").trim()).filter(Boolean);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/site/quote", {
          ride_type: rideType, origin, destination,
          // v2.7.65+E250 · Group rides are priced by Total Riders in Group.
          passengers: rideType === "group" ? (parseInt(groupRiders, 10) || pax) : pax,
          stops: filledStops, pickup_at: pickupIso,
          // v2.7.65+E253 · round-trip multiplies the per-rider group price.
          round_trip: !!roundTrip,
        });
        // Pre-fill the total from the estimate ONLY until the dispatcher edits
        // it; the group breakdown stays live either way as a reference.
        if (data?.subtotal != null && !priceTouched) setTotalCost(String(Number(data.subtotal).toFixed(2)));
        if (rideType === "group" && data?.breakdown) {
          setGroupQuote({ subtotal: data.subtotal, ...data.breakdown });
        } else {
          setGroupQuote(null);
        }
      } catch { setGroupQuote(null); /* silent — dispatcher can type the total manually */ }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatcherMode, priceTouched, origin, destination, pax, rideType, groupRiders, stops, pickupIso, roundTrip]);

  // v2.7.8 · Debounced returning-rider lookup. Triggers when phone has >= 9
  // digits OR email looks valid. We pre-fill the rider's last-known
  // preferred-driver gender so they don't have to re-pick it.
  useEffect(() => {
    if (welcomeDismissed) return undefined;
    const digits = (phone || "").replace(/\D/g, "");
    const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((email || "").trim());
    if (digits.length < 9 && !emailValid) {
      setWelcomeBack(null);
      return undefined;
    }
    const handle = setTimeout(() => {
      api.post("/site/lookup-rider", {
        phone: digits.length >= 9 ? phone.trim() : null,
        email: emailValid ? email.trim() : null,
      }).then(({ data }) => {
        if (data?.recognized) {
          setWelcomeBack({
            first_name: data.first_name,
            trip_count: data.trip_count || 0,
            last_pref: data.last_pref || "any",
            tier: data.tier || "new",
            tier_label: data.tier_label || "New Rider",
          });
          if (data.last_pref && data.last_pref !== "any") {
            // Pre-fill the last-used preference so the rider doesn't have to
            // re-pick it. They can still change it before submitting.
            setPreferredDriverGender((cur) => (cur === "any" ? data.last_pref : cur));
          }
        } else {
          setWelcomeBack(null);
        }
      }).catch(() => { setWelcomeBack(null); });
    }, 500);
    return () => clearTimeout(handle);
  }, [phone, email, welcomeDismissed]);

  // v2.8+E337 · Dialpad live-call auto-populate (dispatch-only).
  // A 5s polling loop + a WS event ('rtg:dialpad_call', re-broadcast by
  // AdminOpsProvider) watch for the newest inbound call. When one arrives we
  // auto-fill the caller IF the form is clean; if the dispatcher is mid-typing
  // we surface a dismissible banner instead (never clobber their work). Unknown
  // numbers open the form in New Customer mode with the number pre-filled.
  const [dialpadCall, setDialpadCall] = useState(null);
  const dialpadSeenRef = useRef(null);
  // Keep a live snapshot of the form-dirty state so the polling/WS handlers
  // (whose closures are pinned by a [dispatcherMode]-only deps array) always
  // read the CURRENT dirtiness — never the empty mount-time state.
  const formDirtyRef = useRef(false);
  useEffect(() => { formDirtyRef.current = isFormDirty(); });
  const applyDialpadCaller = async (rawPhone) => {
    const ph = (rawPhone || "").trim();
    if (!ph) return;
    setDialpadCall(null);
    try {
      const { data } = await adminApi.get("/admin/caller-match", { params: { phone: ph, set_active: 0 } });
      if (data?.matched && data?.customer) {
        const c = data.customer;
        const prefs = data.preferences || {};
        await pickRiderSuggestion({
          id: c.id,
          name: c.name,
          phone: c.phone || ph,
          email: c.email,
          home_address: c.address || prefs.home_address || "",
          favorite_pickup: prefs.favorite_pickup || "",
          customer_type: c.customer_type || prefs.customer_type || "",
        });
        toast.success(`Caller loaded: ${c.name || ph}`);
      } else {
        setName(""); setEmail("");
        setPhone(formatUsPhone(ph));
        setSelectedCustomerId(null);
        setLookupState("new");
        setCustQuery(""); setCustResults([]); setShowCustResults(false);
        toast.message(`New caller — ${formatUsPhone(ph)}`, { description: "No record found. Fill in the details to create a new customer." });
      }
    } catch {
      setPhone(formatUsPhone(ph));
      setLookupState("new");
    }
  };

  useEffect(() => {
    if (!dispatcherMode) return undefined;
    let cancelled = false;

    const handleCall = (call, { force = false } = {}) => {
      if (!call || !call.call_id || !call.phone) return;
      if (!force && call.call_id === dialpadSeenRef.current) return;
      dialpadSeenRef.current = call.call_id;
      if (!force && formDirtyRef.current) {
        setDialpadCall({
          phone: call.phone,
          name: call.customer_name || "",
          matched: !!call.matched,
          trip_count: call.trip_count || 0,
          flags: call.rider_flags || [],
          state: call.state || "",
        });
      } else {
        applyDialpadCaller(call.phone);
      }
    };

    const onWs = (e) => handleCall(e?.detail || null);
    window.addEventListener("rtg:dialpad_call", onWs);

    // Explicit "Load into Dispatcher form" hand-off (caller-match page or the
    // call-activity "Start booking") signals a force-load via sessionStorage —
    // robust against React StrictMode's double-mount (the flag is cleared only
    // by the surviving, non-cancelled mount right before it applies).
    const wantForce = (() => {
      try { return window.sessionStorage.getItem("rtg_dialpad_force") === "1"; }
      catch { return false; }
    })();

    const seedThenPoll = async () => {
      try {
        const { data } = await adminApi.get("/admin/dialpad/latest-call");
        if (cancelled) return;
        if (wantForce && data?.call_id && data?.phone) {
          try { window.sessionStorage.removeItem("rtg_dialpad_force"); } catch { /* ignore */ }
          handleCall(data, { force: true });
        } else {
          dialpadSeenRef.current = data?.call_id || null;
        }
      } catch { /* backend unreachable — poll retries */ }
    };
    seedThenPoll();

    const poll = setInterval(async () => {
      if (cancelled || document.hidden) return;
      try {
        const { data } = await adminApi.get("/admin/dialpad/latest-call");
        if (!cancelled) handleCall(data);
      } catch { /* silent */ }
    }, 5000);

    return () => { cancelled = true; clearInterval(poll); window.removeEventListener("rtg:dialpad_call", onWs); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatcherMode]);

  // PROMPT 2 · Load the caller's ride history for the "Previous Rides" dropdown.
  // Dispatch-only; debounced; fires once the phone has >=7 digits or a valid
  // email is present. Read-only — never mutates a booking.
  useEffect(() => {
    if (!dispatcherMode) return undefined;
    const digits = (phone || "").replace(/\D/g, "");
    const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((email || "").trim());
    const nameQ = (name || "").trim();
    if (digits.length < 7 && !emailValid && nameQ.length < 3) {
      setRideHistory([]); setSelectedHistoryId("");
      return undefined;
    }
    const handle = setTimeout(async () => {
      setRideHistoryLoading(true);
      try {
        const { data } = await adminApi.get("/admin/customer-history", {
          params: {
            phone: digits.length >= 7 ? phone.trim() : "",
            email: emailValid ? email.trim() : "",
            name: (digits.length < 7 && !emailValid) ? nameQ : "",
          },
        });
        setRideHistory(data.trips || []);
      } catch {
        setRideHistory([]);
      } finally {
        setRideHistoryLoading(false);
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [dispatcherMode, phone, email, name]);

  // PROMPT 1 · Language flag label (free text when "Other").
  const languageLabel = () => {
    if (language === "Other") return languageOther.trim() || "Other";
    return language || "";
  };

  // PROMPT 1 · Auto-populated dispatcher-notes block — pickup, drop-off, every
  // stop, rider count, the live total fare, and the language flag. Derived from
  // form state so it updates automatically (e.g. when dispatch changes the fare).
  const autoNotes = useMemo(() => {
    if (!dispatcherMode) return "";
    const lines = [];
    if (origin) lines.push(`Pickup: ${origin}`);
    if (destination) lines.push(`Drop-off: ${destination}`);
    stops.map((s) => (s.value || "").trim()).filter(Boolean)
      .forEach((s, i) => lines.push(`Stop ${i + 1}: ${s}`));
    const riders = rideType === "group" ? (parseInt(groupRiders, 10) || pax) : pax;
    if (riders) lines.push(`Riders: ${riders}`);
    if (totalCost !== "" && !Number.isNaN(parseFloat(totalCost))) {
      lines.push(`Total Fare: $${Number(parseFloat(totalCost)).toFixed(2)}`);
    }
    const lang = languageLabel();
    if (lang) lines.push(`Language: ${lang}`);
    return lines.join("\n");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatcherMode, origin, destination, stops, rideType, groupRiders, pax, totalCost, language, languageOther]);

  // PROMPT 1 · Combine the auto block + the dispatcher's manual notes into the
  // single driver-visible field, without either overwriting the other.
  const combinedDispatcherNotes = () => {
    const manual = dispatcherNotes.trim();
    const auto = autoNotes.trim();
    if (auto && manual) return `${auto}\n\n— Additional Notes —\n${manual}`;
    return auto || manual;
  };

  const addStop = () => {
    if (stops.length >= MAX_STOPS) {
      toast.error(`Maximum of ${MAX_STOPS} stops allowed per booking.`);
      return;
    }
    setStops((s) => [...s, { id: `stop-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, value: "", price: "" }]);
  };
  const updateStop = (idx, val) => setStops((s) => s.map((x, i) => (i === idx ? { ...x, value: val } : x)));
  const updateStopPrice = (idx, val) => setStops((s) => s.map((x, i) => (i === idx ? { ...x, price: val } : x)));
  const removeStop = (idx) => setStops((s) => s.filter((_, i) => i !== idx));

  const validateBasics = () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Name and phone are required.");
      return false;
    }
    if (!origin || !destination) {
      toast.error("Enter pickup and drop-off addresses.");
      return false;
    }
    return true;
  };

  const validateScheduled = () => {
    if (scheduleMode !== "scheduled") {
      // Voucher trips MUST be scheduled — cannot ride ASAP.
      if (isVoucherTrip) {
        toast.error("Voucher trips require booking at least 24 hours in advance and no more than 3 days out per program policy.");
        return { ok: false };
      }
      return { ok: true, iso: null };
    }
    if (!scheduledDate || !scheduledTime) {
      toast.error("Pick a date AND time, or switch to ASAP.");
      return { ok: false };
    }
    const dt = new Date(`${scheduledDate}T${scheduledTime}`);
    if (Number.isNaN(dt.getTime())) {
      toast.error("Schedule date/time looks invalid.");
      return { ok: false };
    }
    if (dt.getTime() < Date.now() - 60_000) {
      toast.error("Scheduled time is in the past.");
      return { ok: false };
    }
    // v2.7.65+E132 · Voucher trips must fall inside the [now+24h, now+72h] window.
    if (isVoucherTrip) {
      const hoursOut = (dt.getTime() - Date.now()) / (1000 * 60 * 60);
      if (hoursOut < 23.95 || hoursOut > 72.05) {
        toast.error("Voucher trips require booking at least 24 hours in advance and no more than 3 days out per program policy.");
        return { ok: false };
      }
    }
    return { ok: true, iso: dt.toISOString() };
  };

  // v2.7.65+E132 · Debounced voucher-serial validation. Fires when the rider
  // types 2+ characters into the serial input. Shows a green/red chip below
  // the input so they get instant feedback before hitting submit.
  useEffect(() => {
    if (!isVoucherTrip) { setVoucherStatus(null); return undefined; }
    const trimmed = (voucherSerial || "").trim();
    if (trimmed.length < 2) { setVoucherStatus(null); return undefined; }
    setVoucherValidating(true);
    const handle = setTimeout(() => {
      api.post("/vouchers/validate-serial", { voucher_serial_number: trimmed })
        .then(({ data }) => {
          setVoucherStatus({ ok: true, rider_first_name: data.rider_first_name || "" });
        })
        .catch((e) => {
          setVoucherStatus({ ok: false, error: formatApiError(e, "Voucher not recognized") });
        })
        .finally(() => setVoucherValidating(false));
    }, 400);
    return () => clearTimeout(handle);
  }, [voucherSerial, isVoucherTrip]);

  // v2.7.65+E132 · Server-side Weatherford geo-fence check right before submit.
  // Returns { ok: true } on pass, { ok: false } on fail (toast already fired).
  const validateVoucherGeofence = async () => {
    if (!isVoucherTrip) return { ok: true };
    try {
      const { data } = await api.post("/vouchers/geofence-check", { origin, destination });
      if (!data?.both_inside) {
        toast.error("CCVAP vouchers are valid exclusively for local travel within Weatherford city limits.");
        return { ok: false };
      }
      return { ok: true };
    } catch (e) {
      toast.error(formatApiError(e, "Could not validate Weatherford city limits."));
      return { ok: false };
    }
  };

  const onQuote = async () => {
    if (!origin || !destination) return toast.error("Enter pickup and drop-off");
    const filledStops = stops.map((s) => (s.value || "").trim()).filter(Boolean);
    if (filledStops.length !== stops.length && stops.length > 0) {
      toast.error("Fill in every stop or remove the empty ones.");
      return;
    }
    setQuoting(true);
    setQuote(null);
    setShowCardForm(false);
    try {
      const { data } = await api.post("/site/quote", {
        ride_type: rideType,
        origin, destination,
        passengers: pax,
        stops: filledStops,
        pickup_at: pickupIso,
        round_trip: dispatcherMode ? !!roundTrip : false,
      });
      setQuote({
        total: data.subtotal,
        miles: data.miles,
        minutes: data.minutes,
        // Surface a few breakdown bits the UI shows next to the total
        stop_count_fee: data?.breakdown?.stop_count_fee || 0,
        stop_count: data?.breakdown?.stop_count || 0,
        late_night: data?.breakdown?.late_night_surcharge || 0,
        late_night_pct: data?.breakdown?.late_night_surcharge_pct || 0,
        holiday: data?.breakdown?.holiday_surcharge || 0,
        holiday_pct: data?.breakdown?.holiday_surcharge_pct || 0,
      });
    } catch (e) {
      toast.error(formatApiError(e, "Could not fetch quote"), { duration: 8000 });
    } finally { setQuoting(false); }
  };

  // No-card path (pay cash at pickup) — same as previous batch DC behavior
  // v2.7.65+E234 · Inline required-field highlight helpers (dispatch-only).
  const markBlurred = (key) => setBlurredFields((p) => ({ ...p, [key]: true }));
  const reqErr = (key, filled) => dispatcherMode && !!blurredFields[key] && !filled;

  // v2.7.65+E234 · Keyboard navigation for the live Name/Phone suggestion list.
  const onSuggestKeyDown = (e, field) => {
    if (!dispatcherMode || !showRiderSuggestions || suggestFor !== field || riderSuggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggestActiveIdx((i) => Math.min(i + 1, riderSuggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSuggestActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (suggestActiveIdx >= 0 && suggestActiveIdx < riderSuggestions.length) {
        e.preventDefault();
        pickRiderSuggestion(riderSuggestions[suggestActiveIdx]);
        setSuggestActiveIdx(-1);
      }
    } else if (e.key === "Escape") {
      setShowRiderSuggestions(false);
      setSuggestActiveIdx(-1);
    }
  };

  const onRequestDispatch = async () => {
    // v2.7.65+E232/E233 · Dispatch booking REQUIRED fields — block submit until
    // every one is filled (first-name, phone, pickup, drop-off, customer type,
    // passengers, ride type, payment method, total cost, dispatcher notes).
    if (dispatcherMode) {
      setBlurredFields({ name: true, phone: true, origin: true, destination: true, customerType: true, pax: true, rideType: true, payment: true, totalCost: true, notes: true });
      if (!name.trim())         { toast.error("First name is required."); return; }
      if (!phone.trim())        { toast.error("Phone number is required."); return; }
      if (!origin.trim())       { toast.error("Pickup address is required."); return; }
      if (!destination.trim())  { toast.error("Drop-off address is required."); return; }
      if (!customerType)        { toast.error("Customer Type is required."); return; }
      if (!pax || pax < 1)      { toast.error("Passenger count is required."); return; }
      if (!rideType)            { toast.error("Ride type is required."); return; }
      if (!dispatchPayment)     { toast.error("Payment method is required."); return; }
      if (totalCost === "" || Number.isNaN(parseFloat(totalCost))) { toast.error("Total Cost (USD) is required."); return; }
      if (!combinedDispatcherNotes()) { toast.error("Add pickup, drop-off and fare, or a dispatcher note."); return; }
    }
    // v2.7.65+E215 · Book-Ahead → Future Fares (dispatcher-only, additive).
    // When the dispatcher flips "Book-Ahead" on a scheduled trip, route it into
    // the isolated Future Fares store instead of the live queue. The backend
    // enforces the >=24h rule; if it returns "not eligible" (<24h) we fall
    // through to the normal dispatch booking below. Public flow is untouched.
    if (dispatcherMode && bookAhead && scheduleMode === "scheduled") {
      if (!validateBasics()) return;
      const schedFF = validateScheduled();
      if (!schedFF.ok) return;
      const parts = name.trim().split(" ");
      const ffPayload = {
        rider: {
          first_name: parts[0] || name.trim(),
          last_name: parts.slice(1).join(" ") || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
        },
        customer_name: name.trim(),
        pickup_address: origin,
        dropoff_address: destination,
        requested_pickup_at: pickupIso,   // naive local → server treats as America/Chicago
        passenger_count: pax,
        fare_total: parseFloat(totalCost) || (quote ? quote.total : 0) || 0,
        subtotal: parseFloat(totalCost) || (quote ? quote.total : 0) || 0,
        payment_method: dispatchPayment || "cash",
        dispatcher_notes: combinedDispatcherNotes() || null,
        assigned_driver_id: assignDriverId || null,
        assigned_driver_name: assignDriverName || null,
        customer_type: customerType || null,
        group_rider_count: rideType === "group" ? (parseInt(groupRiders, 10) || null) : null,
      };
      setSubmitting(true);
      try {
        // v2.7.65+E217 · Use the admin-scoped axios instance so the request
        // carries the ADMIN token (rtg_admin_token). The public `api` instance
        // force-attaches the DRIVER token (rtg_token) via its interceptor,
        // which clobbered a manually-passed admin header and 401'd this
        // dispatcher-only endpoint whenever a driver session existed in the
        // same browser (root cause of "future fare not stored / forces driver").
        const { data } = await adminApi.post("/future-fares", ffPayload);
        toast.success(`Future Fare stored · ${data.future_fare.id}`);
        setConfirmation(null);
        setBookAhead(false);
        setBookAheadTouched(false);
        setSubmitting(false);
        return;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const code = (detail && typeof detail === "object") ? detail.code : null;
        setSubmitting(false);
        if (code === "not_eligible_within_24h") {
          toast.message("Pickup is under 24 hours away — booking through the standard dispatch flow instead.");
          // fall through to the normal dispatch booking below
        } else {
          const msg = (detail && (detail.message || (typeof detail === "string" ? detail : null))) || "Could not store the future fare.";
          toast.error(msg);
          return;
        }
      }
    }
    if (!validateBasics()) return;
    // v2.8 · Dispatch Booking requires a driver to be pre-assigned.
    if (dispatcherMode && !assignDriverId) {
      toast.error("Select a driver to assign before booking.");
      return;
    }
    const sched = validateScheduled();
    if (!sched.ok) return;
    // v2.7.65+E132 · Voucher pre-flight — invalid serial or out-of-city
    // geofence stops the submit BEFORE we touch dispatch.
    if (isVoucherTrip) {
      if (!voucherSerial.trim()) {
        toast.error("Please enter your voucher serial number.");
        return;
      }
      if (voucherStatus && voucherStatus.ok === false) {
        toast.error(voucherStatus.error || "Voucher serial number is not valid.");
        return;
      }
      const gf = await validateVoucherGeofence();
      if (!gf.ok) return;
    }
    const filledStops = stops.map((s) => (s.value || "").trim()).filter(Boolean);
    setSubmitting(true);
    try {
      // v2.7.65+E218 · Dispatcher bookings post via the admin-scoped client so
      // the ADMIN token (rtg_admin_token) is sent for "Booked by" attribution.
      // The public `api` instance force-attaches the DRIVER token (rtg_token),
      // which clobbered attribution whenever a driver session existed in the
      // same browser. Public bookings still use `api` (anonymous).
      const bookingClient = dispatcherMode ? adminApi : api;
      const { data } = await bookingClient.post("/site/quick-booking", {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || null,
        origin, destination,
        passengers: pax,
        ride_type: rideType,
        schedule_mode: scheduleMode,
        scheduled_for: sched.iso,
        stops: filledStops,
        preferred_driver_gender: preferredDriverGender,
        prefers_sms: !!prefersSms,
        prefers_email: !!prefersEmail,
        sms_consent: !!smsConsent,
        sms_consent_ts: smsConsent ? new Date().toISOString() : null,
        is_voucher_trip: !!isVoucherTrip,
        voucher_serial_number: isVoucherTrip ? voucherSerial.trim() : null,
        notes: quote ? `Quoted ~${fmtUsd(quote.total)} (${(quote.miles || 0).toFixed(1)} mi${filledStops.length ? `, ${filledStops.length} stops` : ""})` : "",
        source: BOOKING_SOURCE,
        home_address: dispatcherMode ? (homeAddr.trim() || null) : null,
        customer_type: dispatcherMode ? (customerType || null) : null,
        group_rider_count: dispatcherMode && rideType === "group" ? (parseInt(groupRiders, 10) || null) : null,
        // v2.8 · Admin "+Dispatch Booking" fields — only sent in dispatcher mode.
        ...(dispatcherMode ? {
          assigned_driver_id: assignDriverId || null,
          assigned_driver_name: assignDriverName || null,
          total_cost: parseFloat(totalCost) || 0,
          dispatch_payment_method: dispatchPayment,
          dispatcher_notes: combinedDispatcherNotes() || null,
          round_trip: roundTrip,
          return_reverse: returnReverse,
          return_pickup: roundTrip ? (returnReverse ? destination : (returnPickup.trim() || null)) : null,
          return_dropoff: roundTrip ? (returnReverse ? origin : (returnDropoff.trim() || null)) : null,
          return_pickup_at: (roundTrip && returnDate && returnTime) ? `${returnDate}T${returnTime}` : null,
          stop_prices: stops.map((s) => parseFloat(s.price) || 0),
          rider_flags: riderFlags,
          dispatch_origin: dispatchOrigin,
        } : {}),
      });
      setConfirmation({
        trip_id: data.trip_id,
        status: data.status,
        drivers_pinged: data.drivers_pinged || 0,
        payment: "cash",
        // v2.8 · Dispatcher bookings echo the assigned driver + final total.
        assigned_driver_name: data.assigned_driver_name || null,
        total: data.total != null ? data.total : null,
        booked_by: data.booked_by || null,
      });
      const pingMsg = data.drivers_pinged
        ? ` · Pinging ${data.drivers_pinged} driver${data.drivers_pinged === 1 ? "" : "s"} now`
        : "";
      toast.success(`Dispatch request received · ${data.trip_id}${pingMsg}`);
      // v2.7.65+E247 · Auto-Save Callers — confirm when a brand-new customer
      // record was created for this caller so it's saved for next time.
      if (dispatcherMode && data.customer_saved) {
        toast.success("New caller saved to customers", { icon: "✓" });
      }
    } catch (e) {
      toast.error(formatApiError(e, "Could not submit dispatch request"), { duration: 8000 });
    } finally { setSubmitting(false); }
  };

  // ---- v2.7.0 batch DE · Square auth-hold path -------------------------------
  // Step A: rider clicks "Pay & Book" inside the Quote panel → reveal card form
  const onShowCardForm = () => {
    if (!validateBasics()) return;
    const sched = validateScheduled();
    if (!sched.ok) return;
    setShowCardForm(true);
  };

  // Step B: SquareCardForm tokenizes the card → we call /quick-booking-with-hold
  const onCardTokenized = async (sourceId, details) => {
    if (!quote) {
      toast.error("Get a quote first.");
      return;
    }
    const sched = validateScheduled();
    if (!sched.ok) return;
    // v2.7.65+E132 · Voucher pre-flight (same as no-card path).
    if (isVoucherTrip) {
      if (!voucherSerial.trim()) {
        toast.error("Please enter your voucher serial number.");
        return;
      }
      if (voucherStatus && voucherStatus.ok === false) {
        toast.error(voucherStatus.error || "Voucher serial number is not valid.");
        return;
      }
      const gf = await validateVoucherGeofence();
      if (!gf.ok) return;
    }
    const amountCents = Math.round((quote.total || 0) * 100);
    if (amountCents < 300) {
      toast.error("Quote amount too low to authorize a hold (min $3.00).");
      return;
    }
    if (amountCents > 50000) {
      toast.error("Public hold limit is $500. Please call dispatch for larger trips.");
      return;
    }
    setAuthorizing(true);
    const filledStops = stops.map((s) => (s.value || "").trim()).filter(Boolean);
    try {
      const { data } = await api.post("/site/quick-booking-with-hold", {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || null,
        origin, destination,
        passengers: pax,
        ride_type: rideType,
        schedule_mode: scheduleMode,
        scheduled_for: sched.iso,
        stops: filledStops,
        preferred_driver_gender: preferredDriverGender,
        prefers_sms: !!prefersSms,
        prefers_email: !!prefersEmail,
        sms_consent: !!smsConsent,
        sms_consent_ts: smsConsent ? new Date().toISOString() : null,
        is_voucher_trip: !!isVoucherTrip,
        voucher_serial_number: isVoucherTrip ? voucherSerial.trim() : null,
        notes: `Card hold authorized $${(amountCents / 100).toFixed(2)} · ${(quote.miles || 0).toFixed(1)} mi${filledStops.length ? `, ${filledStops.length} stops` : ""}`,
        source_id: sourceId,
        quoted_amount_cents: amountCents,
        card_brand: details?.card?.brand || details?.brand || "",
        card_last4: details?.card?.last4 || details?.last4 || "",
        card_exp_month: details?.card?.expMonth || details?.expMonth || null,
        card_exp_year: details?.card?.expYear || details?.expYear || null,
        postal_code: details?.billing?.postalCode || null,
        booking_source: BOOKING_SOURCE,
        home_address: dispatcherMode ? (homeAddr.trim() || null) : null,
        customer_type: dispatcherMode ? (customerType || null) : null,
        group_rider_count: dispatcherMode && rideType === "group" ? (parseInt(groupRiders, 10) || null) : null,
      });
      setConfirmation({
        trip_id: data.trip_id,
        status: data.status,
        drivers_pinged: data.drivers_pinged || 0,
        payment: "card_hold",
        hold_amount: amountCents / 100,
        card_brand: data.card_brand,
        card_last4: data.card_last4,
        payment_id: data.payment_id,
      });
      toast.success(`Hold authorized · ${data.trip_id}`, { duration: 8000 });
    } catch (e) {
      toast.error(formatApiError(e, "Card authorization failed"), { duration: 10000 });
    } finally { setAuthorizing(false); }
  };

  const cardStyle = bgUrl
    ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.82), rgba(0,0,0,0.78)), url("${bgUrl}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  if (confirmation) {
    const isCardHold = confirmation.payment === "card_hold";
    return (
      <Card
        className={`border ${isCardHold ? "border-amber-400/70 shadow-amber-500/20" : "border-emerald-400/70 shadow-emerald-500/20"} p-5 shadow-2xl bg-zinc-950/90 backdrop-blur`}
        style={cardStyle}
        data-testid="hero-book-form-confirmation"
      >
        <div className="text-center py-4">
          <div className={`h-14 w-14 rounded-full ${isCardHold ? "bg-amber-400" : "bg-emerald-400"} text-zinc-950 flex items-center justify-center mx-auto mb-3 shadow-lg`}>
            {isCardHold ? <ShieldCheck className="h-7 w-7"/> : <CheckCircle2 className="h-7 w-7"/>}
          </div>
          <div className={`text-[10px] uppercase tracking-[0.3em] ${isCardHold ? "text-amber-300" : "text-emerald-300"} font-bold`}>
            {isCardHold ? "Card Authorized · Hold Placed" : "Dispatch Request Received"}
          </div>
          <div className="text-xl font-black text-zinc-100 mt-1.5">Trip {confirmation.trip_id}</div>
          {/* v2.8 · Dispatcher booking — surface the assigned driver + total. */}
          {confirmation.assigned_driver_name && (
            <div className="mt-3 flex flex-col items-center gap-2" data-testid="hero-confirm-dispatch-summary">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-900 border border-yellow-400/40 text-sm text-yellow-200">
                <Car className="h-4 w-4"/>
                <span>Assigned to <span className="font-bold text-yellow-100" data-testid="hero-confirm-driver">{confirmation.assigned_driver_name}</span></span>
              </div>
              {confirmation.total != null && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-900 border border-emerald-400/40 text-sm text-emerald-200" data-testid="hero-confirm-total">
                  <Calculator className="h-4 w-4"/>
                  <span>Total <span className="font-black text-emerald-100">{fmtUsd(confirmation.total)}</span></span>
                </div>
              )}
            </div>
          )}
          {isCardHold && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-900 border border-amber-400/40 text-xs text-amber-200" data-testid="hero-confirm-card-summary">
              <CreditCard className="h-3.5 w-3.5"/>
              <span className="font-bold tracking-wider">{confirmation.card_brand || "CARD"} •••{confirmation.card_last4 || "????"}</span>
              <span className="text-zinc-500">·</span>
              <span className="text-zinc-300">Hold {fmtUsd(confirmation.hold_amount)}</span>
            </div>
          )}
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed max-w-xs mx-auto">
            {isCardHold
              ? <>We&apos;ll call <span className="text-zinc-200 font-bold">{phone}</span> to confirm and dispatch your driver. Final amount captured when the trip ends.</>
              : <>We&apos;ll call <span className="text-zinc-200 font-bold">{phone}</span> to confirm and dispatch your driver shortly.</>
            }
          </p>
          <Button
            data-testid="hero-confirm-another"
            onClick={() => {
              setConfirmation(null); setQuote(null); setShowCardForm(false);
              setOrigin(""); setDestination(""); setName(""); setPhone(""); setEmail("");
              // v2.7.65+E229 · clear customer-lookup state so the next call starts clean.
              setLookupState(null); setPickupOptions([]); setDropoffOptions([]);
              setCustQuery(""); setCustResults([]); setShowCustResults(false);
              setMostUsedPickup("");
              // v2.7.65+E231 · clear home-address + favorite-pin selection.
              setHomeAddr(""); setSelectedCustomerId(null); setFavoritePickup("");
              setCustomerType("");
              setBlurredFields({}); setSuggestActiveIdx(-1);
              setDupWarning(null); setDupDismissedKey("");
              // v2.8 · Clear dispatcher-mode fields so the next booking starts fresh.
              if (dispatcherMode) {
                setAssignDriverId(""); setAssignDriverName("");
                setTotalCost(""); setPriceTouched(false);
                setDispatchPayment("cash"); setDispatcherNotes(""); setRiderFlags([]);
                setLanguage(""); setLanguageOther("");
                setRideHistory([]); setSelectedHistoryId("");
                setDispatchOrigin("call_in");
                setRoundTrip(false); setReturnReverse(true);
                setReturnPickup(""); setReturnDropoff(""); setReturnDate(""); setReturnTime("");
                setStops([]);
              }
            }}
            className="mt-4 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-wider"
          >
            Book another ride
          </Button>
        </div>
      </Card>
    );
  }

  // v2.8+E313 · Online-booking Hours-of-Operation gate (PUBLIC form ONLY).
  // Dispatcher mode is 24/7 and never gated. When the rider portal is closed
  // the public "Get a Fare in Seconds" widget renders an inert closed card so
  // no online booking can be submitted (the server also enforces this → 403).
  if (!dispatcherMode && portalHours && portalHours.is_open === false) {
    const closedTime = (portalHours.hours_label || "10:00 AM – 3:00 AM").replace(/\s*(CDT|CST)\b/i, "").trim();
    const closedDays = portalHours.days_label || "7 days a week";
    const closedTz = portalHours.tz_abbrev || "CDT";
    return (
      <Card
        className="bg-zinc-950/90 backdrop-blur border border-amber-400/60 p-5 shadow-2xl shadow-amber-500/20"
        style={cardStyle}
        data-testid="hero-book-form-closed"
      >
        <div className="text-center py-4">
          <div className="mx-auto h-14 w-14 rounded-full bg-amber-400/10 border border-amber-400/40 flex items-center justify-center mb-3">
            <Clock className="h-7 w-7 text-amber-300"/>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold">Online Booking Closed</div>
          <div className="text-xl font-black text-zinc-100 mt-1.5" data-testid="hero-closed-heading">We&apos;re Currently Closed</div>
          <p className="text-sm text-zinc-300 mt-3 leading-relaxed max-w-xs mx-auto" data-testid="hero-closed-message">
            {portalHours.closed_message || `Online booking is available ${closedDays}, ${closedTime} ${closedTz}.`}
          </p>
          <div className="mt-4 rounded-lg bg-zinc-900 border border-zinc-800 p-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold mb-1">Booking Hours</div>
            <div className="text-base font-bold text-zinc-100" data-testid="hero-closed-hours">{closedDays}, {closedTime}</div>
            <div className="text-xs text-zinc-500">Central Time ({closedTz})</div>
            {portalHours.next_open_at && (
              <ReopenCountdown
                nextOpenAt={portalHours.next_open_at}
                testId="hero-closed-reopen"
                prefix="Reopens in "
                className="mt-1.5 inline-block text-sm font-bold text-emerald-300"
              />
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-3 flex items-center justify-center gap-1.5" data-testid="hero-closed-phone">
            <Phone className="h-3.5 w-3.5"/> Questions? Call{" "}
            <a href="tel:+14058050199" className="text-amber-300 font-bold hover:text-amber-200">(405) 805-0199</a>
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className="bg-zinc-950/90 backdrop-blur border border-amber-400/60 p-5 shadow-2xl shadow-amber-500/20"
      style={cardStyle}
      data-testid="hero-book-form"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-9 w-9 rounded-lg bg-amber-400 text-zinc-950 flex items-center justify-center shadow-md">
          <Car className="h-5 w-5"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">{dispatcherMode ? "DISPATCH BOOKING SYSTEM" : "Quick Booking"}</div>
          {!dispatcherMode && <h3 className="text-lg font-extrabold text-zinc-100 leading-tight">Get a fare in seconds</h3>}
        </div>
        {/* v2.7.65+E251 · Reset Form — dispatch only, two-step confirm when dirty */}
        {dispatcherMode && (
          resetConfirm ? (
            <div className="ml-auto flex items-center gap-1.5" data-testid="reset-form-confirm-wrap">
              <span className="text-[11px] text-zinc-300 hidden sm:inline">Clear this form?</span>
              <button type="button" onClick={resetForm} data-testid="reset-form-confirm"
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded bg-rose-500 text-white hover:bg-rose-400 transition-colors">
                <RotateCcw className="h-3 w-3"/>Yes, clear
              </button>
              <button type="button" onClick={() => setResetConfirm(false)} data-testid="reset-form-cancel"
                className="text-[11px] text-zinc-400 hover:text-zinc-200 px-1">Cancel</button>
            </div>
          ) : (
            <button type="button" onClick={handleResetClick} data-testid="reset-form-btn"
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded border border-zinc-600 text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors">
              <RotateCcw className="h-3 w-3"/>Reset Form
            </button>
          )
        )}
      </div>

      {/* v2.8+E337 · Dialpad incoming-call banner — shown only when the
          dispatcher is mid-typing and a live call arrives, so their work is
          never clobbered. Tapping "Load caller" pulls the caller in. */}
      {dispatcherMode && dialpadCall && (
        <div
          data-testid="dialpad-incoming-banner"
          className="mb-3 rounded-lg border border-yellow-500/60 bg-yellow-500/10 p-3 flex items-start gap-3 animate-in fade-in slide-in-from-top-1"
        >
          <div className="h-9 w-9 rounded-lg bg-yellow-400 text-zinc-950 flex items-center justify-center shadow flex-shrink-0 animate-pulse">
            <PhoneIncoming className="h-5 w-5"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-yellow-300 font-bold">
              Incoming call{dialpadCall.state ? ` · ${dialpadCall.state}` : ""}
            </div>
            <div className="text-sm font-bold text-zinc-100 truncate" data-testid="dialpad-incoming-name">
              {dialpadCall.matched ? (dialpadCall.name || formatUsPhone(dialpadCall.phone)) : formatUsPhone(dialpadCall.phone)}
            </div>
            <div className="text-[11px] text-zinc-400 truncate">
              {dialpadCall.matched
                ? `Known customer${dialpadCall.trip_count ? ` · ${dialpadCall.trip_count} trip${dialpadCall.trip_count === 1 ? "" : "s"}` : ""}`
                : "New caller — no record on file"}
              {dialpadCall.flags && dialpadCall.flags.length > 0 && (
                <span className="ml-1 text-rose-400 font-bold">· {dialpadCall.flags.length} flag{dialpadCall.flags.length === 1 ? "" : "s"}</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <button
              type="button"
              data-testid="dialpad-incoming-load"
              onClick={() => applyDialpadCaller(dialpadCall.phone)}
              className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded bg-yellow-400 text-zinc-950 hover:bg-yellow-300 transition-colors"
            >
              <PhoneCall className="h-3 w-3"/>Load caller
            </button>
            <button
              type="button"
              data-testid="dialpad-incoming-dismiss"
              onClick={() => setDialpadCall(null)}
              className="text-[11px] text-zinc-400 hover:text-zinc-200 px-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* v2.7.65+E230 · Recent Callers — last 5 distinct callers as 1-tap
          quick-rebook chips (dispatch only). Tapping one autofills the whole
          booking exactly like selecting a search result. */}
      {dispatcherMode && recentCallers.length > 0 && (
        <div className="mb-2" data-testid="recent-callers">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1 flex items-center gap-1">
            <Clock className="h-3 w-3"/>Recent Callers
          </div>
          <div className="flex flex-wrap gap-1.5">
            {recentCallers.map((c, i) => {
              const key = c.id || `${c.phone}-${i}`;
              const trips = historyByKey[key];
              return (
                <HoverCard key={key} openDelay={150} closeDelay={80} onOpenChange={(o) => { if (o) fetchCallerHistory(c, key); }}>
                  <HoverCardTrigger asChild>
                    <button
                      type="button"
                      onClick={() => pickRiderSuggestion(c)}
                      data-testid={`recent-caller-${i}`}
                      title={`${c.name}${c.phone ? " · " + c.phone : ""}`}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-yellow-500/60 hover:text-yellow-200 transition-colors flex items-center gap-1 max-w-[180px]"
                    >
                      <UserCircle2 className="h-3 w-3 flex-shrink-0 text-yellow-400"/>
                      <span className="truncate">{c.name}</span>
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent align="start" className="w-72 bg-zinc-950 border-zinc-700 text-zinc-100 p-3" data-testid={`recent-caller-history-${i}`}>
                    <div className="text-[11px] font-bold text-yellow-200 truncate">{c.name}</div>
                    <div className="text-[10px] text-zinc-400 mb-2 truncate">{c.phone || "no phone"}{c.email ? ` · ${c.email}` : ""}</div>
                    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Last 3 trips</div>
                    {trips === undefined ? (
                      <div className="text-[11px] text-zinc-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin"/>Loading…</div>
                    ) : trips.length === 0 ? (
                      <div className="text-[11px] text-zinc-500 italic">No prior trips on file.</div>
                    ) : (
                      <ul className="space-y-1.5">
                        {trips.map((t, ti) => (
                          <li key={ti} className="text-[11px] leading-tight border-l-2 border-yellow-500/40 pl-2">
                            <div className="text-zinc-300 truncate"><span className="text-emerald-300">{t.from || "—"}</span> → <span className="text-amber-300">{t.to || "—"}</span></div>
                            <div className="text-[10px] text-zinc-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}{t.status ? ` · ${t.status}` : ""}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </HoverCardContent>
                </HoverCard>
              );
            })}
          </div>
        </div>
      )}

      {/* v2.7.65+E229 · Customer auto-lookup search bar — DISPATCH ONLY. Search
          existing customers by name or phone; select to instantly autofill the
          caller's details + pickup (address on file) + address history. The
          public homepage form never renders this (dispatcherMode gate). */}
      {dispatcherMode && (
        <div className="mb-3 relative" data-testid="customer-search-wrap">
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-widest text-yellow-300 font-bold flex items-center gap-1">
              <Search className="h-3 w-3"/>Customer Lookup
            </label>
            {lookupState && (
              <span
                data-testid="customer-lookup-status"
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1 ${
                  lookupState === "found"
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                    : "bg-amber-500/15 text-amber-300 border border-amber-500/40"
                }`}
              >
                {lookupState === "found"
                  ? <><UserCheck className="h-3 w-3"/>Customer Found</>
                  : <><UserPlus className="h-3 w-3"/>New Customer</>}
              </span>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500 pointer-events-none"/>
            <Input
              value={custQuery}
              onChange={(e) => { setCustQuery(e.target.value); setShowCustResults(true); }}
              onFocus={() => { if (custResults.length) setShowCustResults(true); }}
              onBlur={() => setTimeout(() => setShowCustResults(false), 150)}
              placeholder="Search existing customer by name or phone…"
              className="bg-zinc-900 border-yellow-500/40 text-zinc-100 h-9 pl-8"
              data-testid="customer-search-input"
              autoComplete="off"
            />
            {showCustResults && custResults.length > 0 && (
              <div
                className="absolute z-40 left-0 right-0 top-full mt-1 rounded-md border border-yellow-500/40 bg-zinc-950 shadow-lg shadow-yellow-500/10 max-h-64 overflow-y-auto"
                data-testid="customer-search-results"
              >
                {(() => {
                  // v2.7.65+E247 · Detect duplicate customer records (same
                  // phone, 2+ saved rows) among the results → offer a merge.
                  const map = {};
                  custResults.forEach((r) => {
                    if (!r.id) return; // only real saved rows can be merged
                    const d = (r.phone || "").replace(/\D/g, "");
                    if (d.length < 7) return;
                    (map[d] = map[d] || []).push(r);
                  });
                  const groups = Object.entries(map).filter(([, arr]) => arr.length > 1);
                  if (!groups.length) return null;
                  return groups.map(([d, arr]) => (
                    <div key={`dup-${d}`} className="px-2.5 py-2 border-b border-amber-500/30 bg-amber-500/10" data-testid={`customer-dup-banner-${d}`}>
                      <div className="flex items-center gap-1.5 text-[10px] text-amber-200 font-bold">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0"/>
                        {arr.length} duplicate records for {arr[0].name}
                      </div>
                      {mergeConfirmPhone === d ? (
                        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                          <button
                            type="button" disabled={mergeBusy}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => mergeDuplicatesForPhone(d)}
                            data-testid={`customer-dup-merge-confirm-${d}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-amber-400 text-zinc-950 hover:bg-amber-300 disabled:opacity-60"
                          >
                            {mergeBusy ? <Loader2 className="h-3 w-3 animate-spin"/> : <GitMerge className="h-3 w-3"/>}
                            Confirm merge · can&apos;t be undone
                          </button>
                          <button
                            type="button" disabled={mergeBusy}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setMergeConfirmPhone(null)}
                            data-testid={`customer-dup-merge-cancel-${d}`}
                            className="text-[10px] text-zinc-400 hover:text-zinc-200 px-1"
                          >Cancel</button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setMergeConfirmPhone(d)}
                          data-testid={`customer-dup-merge-${d}`}
                          className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-amber-400/50 text-amber-200 hover:bg-amber-400/15"
                        >
                          <GitMerge className="h-3 w-3"/>Merge into one
                        </button>
                      )}
                    </div>
                  ));
                })()}
                <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-yellow-300 font-black border-b border-yellow-500/20">
                  {custResults.length} match{custResults.length === 1 ? "" : "es"} · click to autofill
                </div>
                {custResults.map((r) => (
                  <button
                    key={r.id || `${r.phone}-${r.name}`}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { pickRiderSuggestion(r); setCustQuery(""); }}
                    data-testid={`customer-result-${r.id || r.phone}`}
                    className="w-full text-left px-3 py-2 hover:bg-yellow-500/10 border-b border-zinc-800 last:border-b-0"
                  >
                    <div className="text-sm text-zinc-100 font-semibold truncate flex items-center gap-1.5">
                      {r.name}
                      {r.verified && <span className="text-[9px] uppercase tracking-widest text-emerald-300 font-bold px-1 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30">Verified</span>}
                    </div>
                    <div className="text-[11px] text-zinc-400 truncate">
                      {r.phone || "—"}{r.email ? ` · ${r.email}` : ""}{r.home_address ? ` · ${r.home_address}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* v2.7.8 · Returning-rider greeting · displays as the rider's phone/email
          matches an existing customer. Pre-fills preferred-driver-gender from
          their last booking. Dismissable with the X so first-time-rider feel. */}
      {welcomeBack && !welcomeDismissed && (
        <div
          data-testid="welcome-back-banner"
          className="mb-2.5 rounded-md border border-amber-400/40 bg-gradient-to-r from-amber-400/15 via-amber-400/5 to-transparent px-3 py-2.5 flex items-center gap-3"
        >
          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-amber-400 text-zinc-950 flex items-center justify-center font-extrabold text-sm" data-testid="welcome-back-avatar">
            {welcomeBack.first_name.slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm font-bold text-amber-200" data-testid="welcome-back-greeting">
                Welcome back, {welcomeBack.first_name}!
              </span>
              {/* v2.7.9 · Loyalty tier badge · color follows precious-metal hierarchy */}
              {welcomeBack.tier && welcomeBack.tier !== "new" && (
                <span
                  data-testid="welcome-back-tier"
                  className={
                    welcomeBack.tier === "gold"
                      ? "text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded bg-gradient-to-r from-yellow-400 to-amber-500 text-zinc-950"
                      : welcomeBack.tier === "silver"
                      ? "text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded bg-gradient-to-r from-zinc-300 to-zinc-400 text-zinc-950"
                      : "text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-700 to-orange-800 text-amber-100"
                  }
                  title={
                    welcomeBack.tier === "gold" ? "25+ trips — top-tier rider"
                    : welcomeBack.tier === "silver" ? "10+ trips — frequent rider"
                    : "1-9 trips — building loyalty"
                  }
                >
                  ★ {welcomeBack.tier_label}
                </span>
              )}
            </div>
            <div className="text-[10px] text-amber-200/70 truncate">
              {welcomeBack.trip_count > 0
                ? `${welcomeBack.trip_count} trip${welcomeBack.trip_count === 1 ? "" : "s"} on file · we've pulled in your preferences`
                : "We've loaded your saved preferences"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setWelcomeDismissed(true)}
            className="text-amber-300/60 hover:text-amber-300 transition-colors flex-shrink-0"
            data-testid="welcome-back-dismiss"
            aria-label="Dismiss welcome message"
          >
            <X className="h-4 w-4"/>
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2.5 mb-2.5">
        <div className="col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1"><User className="h-3 w-3 text-amber-400"/>Your Name{dispatcherMode && <span className="text-rose-400 normal-case"> *</span>}</label>
          <div className="relative">
            <Input
              value={name}
              onChange={(e)=>{ setName(e.target.value); if (dispatcherMode) { setShowRiderSuggestions(true); setSuggestFor("name"); } }}
              onFocus={()=>{ if (dispatcherMode && riderSuggestions.length) { setShowRiderSuggestions(true); setSuggestFor("name"); } }}
              onKeyDown={(e)=>onSuggestKeyDown(e, "name")}
              onBlur={()=>{ if (dispatcherMode) markBlurred("name"); setTimeout(()=>setShowRiderSuggestions(false), 150); }}
              placeholder="Full name"
              className={`bg-zinc-900 text-zinc-100 h-9 ${reqErr("name", name.trim()) ? "border-rose-500" : "border-zinc-700"}`}
              data-testid="hero-name"
              autoComplete="off"
            />
            {dispatcherMode && showRiderSuggestions && suggestFor === "name" && riderSuggestions.length > 0 && (
              <div
                className="absolute z-30 left-0 right-0 top-full mt-1 rounded-md border border-yellow-500/40 bg-zinc-950 shadow-lg shadow-yellow-500/10 max-h-64 overflow-y-auto"
                data-testid="rider-search-suggestions"
              >
                <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-yellow-300 font-black border-b border-yellow-500/20">
                  {riderSuggestions.length} known rider{riderSuggestions.length === 1 ? "" : "s"} · click to autofill
                </div>
                {riderSuggestions.map((r, idx) => (
                  <button
                    key={r.id || `${r.phone}-${r.name}`}
                    type="button"
                    onMouseDown={(e)=>e.preventDefault()}
                    onMouseEnter={()=>setSuggestActiveIdx(idx)}
                    onClick={()=>pickRiderSuggestion(r)}
                    data-testid={`rider-suggestion-${r.id || r.phone}`}
                    className={`w-full text-left px-3 py-2 border-b border-zinc-800 last:border-b-0 ${idx === suggestActiveIdx ? "bg-yellow-500/20" : "hover:bg-yellow-500/10"}`}
                  >
                    <div className="text-sm text-zinc-100 font-semibold truncate flex items-center gap-1.5">
                      {r.name}
                      {r.verified && <span className="text-[9px] uppercase tracking-widest text-emerald-300 font-bold px-1 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30">Verified</span>}
                    </div>
                    <div className="text-[11px] text-zinc-400 truncate">
                      {r.phone || "—"}{r.email ? ` · ${r.email}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {reqErr("name", name.trim()) && <div className="text-[9px] text-rose-400 mt-0.5" data-testid="hero-name-required-hint">First name is required</div>}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1"><Phone className="h-3 w-3 text-amber-400"/>Phone{dispatcherMode && <span className="text-rose-400 normal-case"> *</span>}</label>
          <div className="relative">
            <Input
              value={phone}
              onChange={(e)=>{ setPhone(dispatcherMode ? formatUsPhone(e.target.value) : e.target.value); if (dispatcherMode) { setShowRiderSuggestions(true); setSuggestFor("phone"); } }}
              onFocus={()=>{ if (dispatcherMode && riderSuggestions.length) { setShowRiderSuggestions(true); setSuggestFor("phone"); } }}
              onKeyDown={(e)=>onSuggestKeyDown(e, "phone")}
              onBlur={()=>{ if (dispatcherMode) markBlurred("phone"); setTimeout(()=>setShowRiderSuggestions(false), 150); }}
              placeholder="(580) 555-1212"
              className={`bg-zinc-900 text-zinc-100 h-9 ${reqErr("phone", phone.trim()) ? "border-rose-500" : "border-zinc-700"}`}
              data-testid="hero-phone"
              autoComplete="off"
            />
            {dispatcherMode && showRiderSuggestions && suggestFor === "phone" && riderSuggestions.length > 0 && (
              <div
                className="absolute z-30 left-0 right-0 top-full mt-1 rounded-md border border-yellow-500/40 bg-zinc-950 shadow-lg shadow-yellow-500/10 max-h-64 overflow-y-auto"
                data-testid="rider-search-suggestions-phone"
              >
                <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-yellow-300 font-black border-b border-yellow-500/20">
                  {riderSuggestions.length} known rider{riderSuggestions.length === 1 ? "" : "s"} · click to autofill
                </div>
                {riderSuggestions.map((r, idx) => (
                  <button
                    key={r.id || `${r.phone}-${r.name}`}
                    type="button"
                    onMouseDown={(e)=>e.preventDefault()}
                    onMouseEnter={()=>setSuggestActiveIdx(idx)}
                    onClick={()=>pickRiderSuggestion(r)}
                    data-testid={`rider-suggestion-phone-${r.id || r.phone}`}
                    className={`w-full text-left px-3 py-2 border-b border-zinc-800 last:border-b-0 ${idx === suggestActiveIdx ? "bg-yellow-500/20" : "hover:bg-yellow-500/10"}`}
                  >
                    <div className="text-sm text-zinc-100 font-semibold truncate flex items-center gap-1.5">
                      {r.name}
                      {r.verified && <span className="text-[9px] uppercase tracking-widest text-emerald-300 font-bold px-1 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30">Verified</span>}
                    </div>
                    <div className="text-[11px] text-zinc-400 truncate">
                      {r.phone || "—"}{r.email ? ` · ${r.email}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {reqErr("phone", phone.trim()) && <div className="text-[9px] text-rose-400 mt-0.5" data-testid="hero-phone-required-hint">Phone number is required</div>}
          {dispatcherMode && bannedInfo && (
            <div className="mt-1.5 rounded-md border-2 border-red-500 bg-red-600/20 px-2.5 py-2 flex items-start gap-2" data-testid="banned-rider-warning">
              <AlertTriangle className="h-4 w-4 text-red-300 mt-0.5 flex-shrink-0 animate-pulse"/>
              <div className="text-[11px] leading-snug">
                <div className="font-black text-red-200 uppercase tracking-wide">Banned Rider match</div>
                <div className="text-red-100/90" data-testid="banned-rider-warning-detail">
                  This phone was previously flagged <span className="font-bold">Banned Rider</span>
                  {bannedInfo.rider_name ? ` (${bannedInfo.rider_name})` : ""}
                  {bannedInfo.last_flagged_at ? ` · last flagged ${new Date(bannedInfo.last_flagged_at).toLocaleDateString()}` : ""}.
                  The "Banned Rider" flag has been auto-added below — you can still book if you choose.
                </div>
              </div>
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
            <Mail className="h-3 w-3 text-amber-400"/>Email <span className="text-zinc-600 normal-case tracking-normal">(optional)</span>
          </label>
          <Input value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="you@email.com" className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9" data-testid="hero-email"/>
        </div>
        {/* v2.7.65+E231 · Optional home address — saved to the caller's record so
            brand-new callers get an address on file immediately (dispatch only). */}
        {dispatcherMode && (
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
              <MapPinned className="h-3 w-3 text-yellow-400"/>Home Address <span className="text-zinc-600 normal-case tracking-normal">(optional · saved to file)</span>
            </label>
            <Input value={homeAddr} onChange={(e)=>setHomeAddr(e.target.value)} placeholder="123 Main St, Weatherford, OK 73096" className="bg-zinc-900 border-yellow-500/30 text-zinc-100 h-9" data-testid="hero-home-address"/>
          </div>
        )}
        {/* v2.7.65+E232 · Required Customer Type classification (dispatch only). */}
        {dispatcherMode && (
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
              <Users className="h-3 w-3 text-yellow-400"/>Customer Type <span className="text-rose-400 normal-case">*</span>
            </label>
            <Select value={customerType} onValueChange={setCustomerType}>
              <SelectTrigger data-testid="hero-customer-type" className={`bg-zinc-900 h-9 text-zinc-100 ${customerType ? "border-yellow-500/30" : "border-rose-500/50"}`}>
                <SelectValue placeholder="Select customer type (required)"/>
              </SelectTrigger>
              <SelectContent>
                {CUSTOMER_TYPES.map((ct) => (
                  <SelectItem key={ct} value={ct} data-testid={`customer-type-opt-${ct.replace(/\s+/g, "-").toLowerCase()}`}>{ct}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* PROMPT 2 · Customer Ride History — read-only. Lists the caller's past
          trips (by Trip ID); selecting one shows full detail. Dispatch-only. */}
      {dispatcherMode && (rideHistoryLoading || rideHistory.length > 0) && (
        <div className="mb-2.5" data-testid="ride-history-section">
          <label className="text-[10px] uppercase tracking-widest text-yellow-200 font-bold flex items-center gap-1 mb-1">
            <Clock className="h-3 w-3 text-yellow-300"/>Previous Rides
            {rideHistory.length > 0 && <span className="text-yellow-400 normal-case tracking-normal">({rideHistory.length})</span>}
            <span className="text-zinc-600 normal-case tracking-normal">· read-only history</span>
          </label>
          <select
            value={selectedHistoryId}
            onChange={(e) => setSelectedHistoryId(e.target.value)}
            disabled={rideHistoryLoading || rideHistory.length === 0}
            className="w-full bg-zinc-900 border border-yellow-500/30 rounded px-3 py-2 text-sm text-zinc-100 disabled:opacity-60"
            data-testid="ride-history-select"
          >
            <option value="">
              {rideHistoryLoading
                ? "Loading previous rides…"
                : `${rideHistory.length} previous ride${rideHistory.length === 1 ? "" : "s"} — select a Trip ID to view…`}
            </option>
            {rideHistory.map((r) => {
              const d = r.created_at ? new Date(r.created_at) : null;
              const ds = d && !isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
              return (
                <option key={r.record_id} value={r.record_id} data-testid={`ride-history-opt-${r.record_id}`}>
                  {r.trip_id}{ds ? ` · ${ds}` : ""}{r.status ? ` · ${r.status}` : ""}
                </option>
              );
            })}
          </select>
          {(() => {
            const sel = rideHistory.find((r) => r.record_id === selectedHistoryId);
            if (!sel) return null;
            const d = sel.created_at ? new Date(sel.created_at) : null;
            const valid = d && !isNaN(d.getTime());
            const dateStr = valid ? d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—";
            const timeStr = valid ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "—";
            const stopsList = Array.isArray(sel.stops) ? sel.stops.filter(Boolean) : [];
            const row = (label, value, tid) => (
              <div key={label} className="flex flex-col gap-0.5 py-1 border-b border-yellow-500/10 last:border-0">
                <span className="text-[9px] uppercase tracking-widest text-yellow-400/70 font-bold">{label}</span>
                <span className="text-[12px] text-zinc-100 break-words" data-testid={tid}>{value}</span>
              </div>
            );
            return (
              <div className="mt-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3" data-testid="ride-history-detail">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-mono text-[12px] text-yellow-200 font-bold" data-testid="ride-history-detail-id">{sel.trip_id}</span>
                  {sel.status && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-200 border border-yellow-500/30">{sel.status}</span>}
                  <span className="ml-auto text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Read-only</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  {row("Pickup", sel.pickup || "—", "ride-history-detail-pickup")}
                  {row("Drop-off", sel.dropoff || "—", "ride-history-detail-dropoff")}
                  {row("Stops", stopsList.length ? stopsList.join(" · ") : (sel.extra_stops ? `${sel.extra_stops} stop(s) — addresses not recorded` : "None"), "ride-history-detail-stops")}
                  {row("Riders", sel.riders != null ? String(sel.riders) : "—", "ride-history-detail-riders")}
                  {row("Fare", (sel.total != null && sel.total !== "") ? fmtUsd(Number(sel.total)) : "—", "ride-history-detail-fare")}
                  {row("Date", dateStr, "ride-history-detail-date")}
                  {row("Time", timeStr, "ride-history-detail-time")}
                  {row("Driver", sel.driver_name || "Unassigned", "ride-history-detail-driver")}
                  {row("Dispatcher", sel.dispatcher_name || "—", "ride-history-detail-dispatcher")}
                </div>
                <div className="mt-1">
                  {row("Notes", sel.notes ? <span className="whitespace-pre-wrap">{sel.notes}</span> : "—", "ride-history-detail-notes")}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      <div className="space-y-2.5">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1"><MapPin className="h-3 w-3 text-amber-400"/>Pickup{dispatcherMode && <span className="text-rose-400 normal-case"> *</span>}</label>
          <div className={reqErr("origin", origin.trim()) ? "rounded-md ring-2 ring-rose-500/70" : ""} onBlur={()=>{ if (dispatcherMode) markBlurred("origin"); }}>
            <AddressAutocomplete value={origin} onChange={setOrigin} placeholder="Where from?" testid="hero-origin"/>
          </div>
          {reqErr("origin", origin.trim()) && <div className="text-[9px] text-rose-400 mt-0.5" data-testid="hero-origin-required-hint">Pickup address is required</div>}
          {/* v2.7.65+E229/E231 · Pickup options — pinned home (Pin) always first
              & default, most-used (Star) next, address on file last. The pin
              toggle lets dispatch set the caller's true home (dispatch only). */}
          {dispatcherMode && pickupOptions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="pickup-address-options">
              {pickupOptions.map((a, i) => {
                const isFav = favoritePickup && a.toLowerCase() === favoritePickup.toLowerCase();
                const isMost = !isFav && mostUsedPickup && a.toLowerCase() === mostUsedPickup.toLowerCase();
                const active = origin === a;
                return (
                  <div key={i} className={`flex items-center rounded border ${active ? "border-yellow-400 bg-yellow-500/15" : "border-zinc-700 bg-zinc-900"}`}>
                    <button
                      type="button"
                      onClick={() => setOrigin(a)}
                      data-testid={`pickup-option-${i}`}
                      title={a}
                      className={`text-[10px] leading-tight pl-2 pr-1 py-1 max-w-[220px] flex items-center ${active ? "text-yellow-200" : "text-zinc-300"}`}
                    >
                      {isFav && <Pin className="h-3 w-3 mr-0.5 text-yellow-300 flex-shrink-0" fill="currentColor"/>}
                      {isMost && <Star className="h-3 w-3 mr-0.5 text-amber-300 flex-shrink-0" fill="currentColor"/>}
                      <span className="truncate">{a}</span>
                    </button>
                    {selectedCustomerId && (
                      <button
                        type="button"
                        onClick={() => toggleFavoritePickup(a)}
                        data-testid={`pickup-pin-${i}`}
                        title={isFav ? "Unpin home pickup" : "Pin as this caller's home pickup"}
                        className={`px-1.5 py-1 border-l ${active ? "border-yellow-400/40" : "border-zinc-700"} ${isFav ? "text-yellow-300" : "text-zinc-500 hover:text-yellow-300"}`}
                      >
                        <Pin className="h-3 w-3" fill={isFav ? "currentColor" : "none"}/>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1"><MapPin className="h-3 w-3 text-amber-400"/>Drop-off{dispatcherMode && <span className="text-rose-400 normal-case"> *</span>}</label>
          <div className={reqErr("destination", destination.trim()) ? "rounded-md ring-2 ring-rose-500/70" : ""} onBlur={()=>{ if (dispatcherMode) markBlurred("destination"); }}>
            <AddressAutocomplete value={destination} onChange={setDestination} placeholder="Where to?" testid="hero-destination"/>
          </div>
          {reqErr("destination", destination.trim()) && <div className="text-[9px] text-rose-400 mt-0.5" data-testid="hero-destination-required-hint">Drop-off address is required</div>}
          {/* v2.7.65+E229 · Previously-used drop-offs (dispatch only) */}
          {dispatcherMode && dropoffOptions.length > 1 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="dropoff-address-options">
              {dropoffOptions.map((a, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setDestination(a)}
                  data-testid={`dropoff-option-${i}`}
                  title={a}
                  className={`text-[10px] leading-tight px-2 py-1 rounded border max-w-full truncate ${
                    destination === a
                      ? "border-yellow-400 bg-yellow-500/15 text-yellow-200"
                      : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-yellow-500/50"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* v2.7.0 batch DH · Multi-stop UI (up to MAX_STOPS) ============================ */}
        {stops.length > 0 && (
          <div className="space-y-1.5" data-testid="hero-stops-list">
            {stops.map((stop, idx) => (
              <div key={stop.id} className="flex items-end gap-1.5">
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
                    <MapPin className="h-3 w-3 text-amber-400"/>Stop {idx + 1}
                  </label>
                  <AddressAutocomplete
                    value={stop.value}
                    onChange={(v) => updateStop(idx, v)}
                    placeholder={`Add stop #${idx + 1}…`}
                    testid={`hero-stop-${idx}`}
                  />
                </div>
                {dispatcherMode && (
                  <div className="w-24">
                    <label className="text-[10px] uppercase tracking-widest text-yellow-200 font-bold flex items-center gap-1 mb-1">
                      Price
                    </label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">$</span>
                      <Input
                        type="number" min="0" step="0.01" inputMode="decimal"
                        value={stop.price}
                        onChange={(e) => updateStopPrice(idx, e.target.value)}
                        placeholder="0.00"
                        className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 pl-5 text-xs"
                        data-testid={`hero-stop-${idx}-price`}
                      />
                    </div>
                  </div>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeStop(idx)}
                  className="text-red-300 hover:bg-red-900/20 h-9 px-2"
                  data-testid={`hero-stop-${idx}-remove`}
                >
                  <X className="h-4 w-4"/>
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button" variant="outline" onClick={addStop}
            disabled={stops.length >= MAX_STOPS}
            className="border-amber-400/50 bg-zinc-900 text-amber-300 hover:bg-amber-400/10 text-xs h-8 px-2.5"
            data-testid="hero-add-stop"
          >
            <Plus className="h-3.5 w-3.5 mr-1"/>
            {stops.length === 0 ? "Add a stop" : `Add another stop (${stops.length}/${MAX_STOPS})`}
          </Button>
          {stops.length > 0 && (
            <span className="text-[10px] text-amber-300 font-bold" data-testid="hero-stops-summary">
              {stops.length} stop{stops.length === 1 ? "" : "s"} · +${(stops.length * 2).toFixed(2)} stop fee
            </span>
          )}
        </div>
        {/* Permanent multi-stop fee notice */}
        <div className="text-[10px] text-zinc-400 bg-zinc-900/70 border border-zinc-800 rounded px-2.5 py-1.5 flex items-start gap-1.5" data-testid="hero-stops-notice">
          <Info className="h-3 w-3 text-amber-400 flex-shrink-0 mt-0.5"/>
          <span><span className="text-amber-300 font-bold">$2.00 fee per stop</span> · <span className="text-amber-300 font-bold">$1.50 per minute wait fee</span> per stop — <span className="text-zinc-200 font-semibold">tracked by the Driver App and Taxi Meter</span>.</span>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1"><Users className="h-3 w-3 text-amber-400"/>Passengers{dispatcherMode && <span className="text-rose-400 normal-case"> *</span>}</label>
            <select
              value={pax}
              onChange={(e) => setPax(parseInt(e.target.value, 10))}
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100"
              data-testid="hero-pax"
            >
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n === 1 ? "1 passenger" : `${n} passengers`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1"><Car className="h-3 w-3 text-amber-400"/>Ride Type{dispatcherMode && <span className="text-rose-400 normal-case"> *</span>}</label>
            <select
              value={rideType}
              onChange={(e) => setRideType(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100"
              data-testid="hero-ridetype"
            >
              <option value="single">Single Ride</option>
              <option value="group">Group Ride</option>
            </select>
          </div>
        </div>

        {/* v2.7.65+E250 · Total Riders in Group — dispatch-only, shown for group rides */}
        {dispatcherMode && rideType === "group" && (
          <div data-testid="hero-group-riders-wrap">
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
              <Users className="h-3 w-3 text-violet-400"/>Total Riders in Group
              <span className="ml-1 text-[9px] text-zinc-500 font-normal normal-case tracking-normal">(group discounts start at 6)</span>
            </label>
            <input
              type="number"
              min={1}
              inputMode="numeric"
              value={groupRiders}
              onChange={(e) => setGroupRiders(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="e.g. 6"
              className="w-full bg-zinc-900 border border-violet-500/40 rounded px-3 py-2 text-sm text-zinc-100 focus:border-violet-400 outline-none"
              data-testid="hero-group-riders"
            />
          </div>
        )}

        {/* v2.7.7 · Gender-Preferred Dispatch — safety preference dropdown. */}
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
            <UserCircle2 className="h-3 w-3 text-amber-400"/>Preferred Driver Gender
            <span className="ml-1 text-[9px] text-zinc-500 font-normal normal-case tracking-normal">(optional · for rider safety)</span>
          </label>
          <select
            value={preferredDriverGender}
            onChange={(e) => setPreferredDriverGender(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100"
            data-testid="hero-preferred-driver-gender"
          >
            <option value="any">No Preference</option>
            <option value="male">Preferred Male Driver</option>
            <option value="female">Preferred Female Driver</option>
          </select>
          {preferredDriverGender !== "any" && (
            <div className="mt-1.5 text-[10px] text-amber-300/90 bg-amber-400/10 border border-amber-400/30 rounded px-2 py-1 flex items-start gap-1.5">
              <ShieldCheck className="h-3 w-3 flex-shrink-0 mt-0.5"/>
              <span>Dispatch will prioritize a {preferredDriverGender} driver. If none is available we&apos;ll send the next closest driver so you&apos;re not stranded.</span>
            </div>
          )}
        </div>

        {/* v2.7.65+E91 · Per-booking notification override (SMS / Email) */}
        {!dispatcherMode && (
        <div data-testid="hero-notification-prefs">
          <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
            <Send className="h-3 w-3 text-amber-400"/>How should we reach you about this booking?
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 cursor-pointer hover:border-amber-400/40 transition">
              <input
                type="checkbox"
                data-testid="hero-prefers-sms"
                checked={prefersSms}
                onChange={(e) => setPrefersSms(e.target.checked)}
                className="h-4 w-4 rounded accent-amber-400"
              />
              <span className="text-xs text-zinc-100">Text me (SMS)</span>
            </label>
            <label className="flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 cursor-pointer hover:border-amber-400/40 transition">
              <input
                type="checkbox"
                data-testid="hero-prefers-email"
                checked={prefersEmail}
                onChange={(e) => setPrefersEmail(e.target.checked)}
                className="h-4 w-4 rounded accent-amber-400"
              />
              <span className="text-xs text-zinc-100">Email me</span>
            </label>
          </div>
          {!prefersSms && !prefersEmail && (
            <div className="mt-1.5 text-[10px] text-amber-300/90 bg-amber-400/10 border border-amber-400/30 rounded px-2 py-1 flex items-start gap-1.5" data-testid="hero-no-channels-warning">
              <Info className="h-3 w-3 flex-shrink-0 mt-0.5"/>
              <span>Both channels are off — we&apos;ll only reach you by phone call for urgent updates.</span>
            </div>
          )}
        </div>
        )}

        {/* ── v2.8 · Admin "+Dispatch Booking" fields (dispatcher-mode only) ── */}
        {dispatcherMode && (
          <div className="rounded-lg border-2 border-yellow-400/40 bg-yellow-950/10 p-3 space-y-3" data-testid="dispatch-fields">
            {/* Driver assignment (same roster/logic as the Ride Coordinator) */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-yellow-200 font-bold flex items-center justify-between gap-1 mb-1">
                <span className="flex items-center gap-1"><Car className="h-3 w-3 text-yellow-300"/>Assign Driver <span className="text-rose-300">*</span></span>
                <span className="normal-case tracking-normal text-zinc-500 flex items-center gap-1" data-testid="dispatch-driver-count">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"/>
                  {dispDrivers.filter((d) => d.online).length} online · {dispDrivers.length} active
                </span>
              </label>
              <select
                value={assignDriverId}
                onChange={(e) => {
                  const val = e.target.value;
                  setAssignDriverId(val);
                  setAssignDriverName((dispDrivers.find((d) => d.id === val)?.name) || "");
                }}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100"
                data-testid="dispatch-driver-select"
              >
                <option value="">Choose a driver…</option>
                {dispDrivers.filter((d) => d.online).length > 0 && (
                  <optgroup label="🟢 Online">
                    {dispDrivers.filter((d) => d.online).map((d) => (
                      <option key={d.id} value={d.id}>{d.name} · {d.phone}</option>
                    ))}
                  </optgroup>
                )}
                {dispDrivers.filter((d) => !d.online).length > 0 && (
                  <optgroup label="⚪ Offline / not pinging">
                    {dispDrivers.filter((d) => !d.online).map((d) => (
                      <option key={d.id} value={d.id}>{d.name} · {d.phone}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              {dispDrivers.length === 0 && (
                <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1" data-testid="dispatch-driver-loading">
                  <Loader2 className="h-3 w-3 animate-spin"/>Loading active drivers… (refreshes automatically)
                </div>
              )}
            </div>

            {/* Payment method */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-yellow-200 font-bold flex items-center gap-1 mb-1">
                <CreditCard className="h-3 w-3 text-yellow-300"/>Payment Method <span className="text-rose-400 normal-case">*</span>
              </label>
              <select
                value={dispatchPayment}
                onChange={(e) => setDispatchPayment(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100"
                data-testid="dispatch-payment-method"
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="already_processed">Payment Already Processed</option>
              </select>
              {dispatchPayment === "already_processed" && (
                <div className="mt-1.5 text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded px-2 py-1 flex items-start gap-1.5" data-testid="dispatch-paid-note">
                  <CheckCircle2 className="h-3 w-3 flex-shrink-0 mt-0.5"/>
                  <span>Trip will be marked <span className="font-bold">PAID</span> — the driver sees &ldquo;PAID — do not collect&rdquo;.</span>
                </div>
              )}
            </div>

            {/* PROMPT 1 · Total Cost — ALWAYS editable. The live estimate only
                pre-fills until the dispatcher types; "Use auto estimate" restores
                it on demand. Dispatch has full manual control at all times. */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-yellow-200 font-bold flex items-center justify-between gap-1 mb-1">
                <span className="flex items-center gap-1"><Calculator className="h-3 w-3 text-yellow-300"/>Total Cost (USD) <span className="text-rose-400 normal-case">*</span></span>
                {priceTouched && (
                  <button type="button" onClick={()=>setPriceTouched(false)} data-testid="dispatch-total-auto"
                    className="normal-case tracking-normal inline-flex items-center gap-1 text-[10px] font-bold text-yellow-300 hover:text-yellow-200 border border-yellow-500/40 rounded px-1.5 py-0.5">
                    <RotateCcw className="h-3 w-3"/>Use auto estimate
                  </button>
                )}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                <Input
                  type="number" min="0" step="0.01" inputMode="decimal"
                  value={totalCost}
                  onChange={(e) => { setPriceTouched(true); setTotalCost(e.target.value); }}
                  onBlur={()=>{ if (dispatcherMode) markBlurred("totalCost"); }}
                  placeholder="0.00"
                  className={`bg-zinc-900 text-zinc-100 h-9 pl-6 pr-8 meter-display ${reqErr("totalCost", totalCost !== "" && !Number.isNaN(parseFloat(totalCost))) ? "border-rose-500" : "border-zinc-700"}`}
                  data-testid="dispatch-total-cost"
                />
              </div>
              <div className="text-[9px] text-zinc-500 mt-1" data-testid="dispatch-total-hint">
                {priceTouched
                  ? "Manual fare — this is the final price sent to the driver."
                  : "Auto-estimated from the route — type any time to set your own fare."}
              </div>
              {reqErr("totalCost", totalCost !== "" && !Number.isNaN(parseFloat(totalCost))) && <div className="text-[9px] text-rose-400 mt-0.5" data-testid="dispatch-total-required-hint">A valid total cost is required</div>}
            </div>

            {/* v2.7.65+E248 · Live Group-fare per-passenger breakdown */}
            {dispatcherMode && rideType === "group" && groupQuote && (() => {
              const gq = groupQuote;
              const basePp = Number(gq.price_per_passenger) || 0;
              const oneWayPp = gq.one_way_price_per_rider != null ? Number(gq.one_way_price_per_rider) : basePp;
              const effPp = gq.effective_price_per_passenger != null ? Number(gq.effective_price_per_passenger) : oneWayPp;
              const paxN = Number(gq.passengers) || Number(pax) || 1;
              const isRT = !!gq.round_trip;
              const rtMult = Number(gq.round_trip_multiplier) || 1;
              const ridersSubtotal = gq.riders_subtotal != null ? Number(gq.riders_subtotal) : effPp * paxN;
              const bookingFee = Number(gq.group_booking_fee) || 0;
              const tierMin = gq.discount_tier_applied || null;
              const items = [];
              const push = (key, label) => { const v = Number(gq[key]); if (v > 0) items.push({ label, val: v }); };
              push("airport_fee", "Airport fee");
              push("toll_fee", "Toll fee");
              if (Number(gq.stop_count_fee) > 0) items.push({ label: `Stops (${gq.stop_count || 0})`, val: Number(gq.stop_count_fee) });
              push("local_franchise_fee", "Franchise fee");
              if (Number(gq.tech_fee) > 0) items.push({ label: `Technology fee${gq.tech_fee_passengers ? ` (\u00d7${gq.tech_fee_passengers})` : ""}`, val: Number(gq.tech_fee) });
              push("late_night_surcharge", "Late-night surcharge");
              push("holiday_surcharge", "Holiday surcharge");
              push("taxes_total", "Taxes");
              const itemized = items.reduce((s, i) => s + i.val, 0);
              const other = Number((Number(gq.subtotal) - ridersSubtotal - bookingFee - itemized).toFixed(2));
              return (
                <div className="rounded-md border border-violet-500/40 bg-violet-500/10 p-2.5" data-testid="dispatch-group-breakdown">
                  <div className="text-[10px] uppercase tracking-widest text-violet-200 font-bold flex items-center gap-1 mb-1.5">
                    <Users className="h-3 w-3 text-violet-300"/>Group Fare · per-rider{isRT ? " · round trip" : ""}
                  </div>
                  <div className="space-y-1 text-[11px]">
                    {gq.bracket && (
                      <div className="flex justify-between text-zinc-400">
                        <span>Rate bracket</span>
                        <span className="text-zinc-200" data-testid="group-bracket">{gq.bracket}{gq.miles != null ? ` \u00b7 ${gq.miles} mi` : ""}</span>
                      </div>
                    )}
                    {tierMin != null && (
                      <div className="flex justify-between text-emerald-300" data-testid="group-tier-badge">
                        <span>Volume discount</span>
                        <span className="font-semibold">{tierMin}+ riders tier</span>
                      </div>
                    )}
                    {isRT && (
                      <div className="flex justify-between text-zinc-400">
                        <span>Per-rider (round trip)</span>
                        <span className="text-zinc-300"><span data-testid="group-oneway-per-rider">${oneWayPp.toFixed(2)}</span> × {rtMult} = ${effPp.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-zinc-300">
                        <span className="text-violet-200 font-semibold" data-testid="group-per-passenger">${effPp.toFixed(2)}</span>
                        <span className="text-zinc-500"> × {paxN} rider{paxN === 1 ? "" : "s"}</span>
                      </span>
                      <span className="text-zinc-100 font-semibold" data-testid="group-base-total">${Number(ridersSubtotal).toFixed(2)}</span>
                    </div>
                    {bookingFee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-300">Booking fee <span className="text-zinc-500">(group, once)</span></span>
                        <span className="text-zinc-100 font-semibold" data-testid="group-booking-fee">${bookingFee.toFixed(2)}</span>
                      </div>
                    )}
                    {items.map((it, i) => (
                      <div key={i} className="flex justify-between text-zinc-400">
                        <span>{it.label}</span>
                        <span className="text-zinc-300">${it.val.toFixed(2)}</span>
                      </div>
                    ))}
                    {other > 0.01 && (
                      <div className="flex justify-between text-zinc-400">
                        <span>Other fees &amp; taxes</span>
                        <span className="text-zinc-300">${other.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-violet-500/30 pt-1 mt-1">
                      <span className="text-violet-200 font-bold uppercase text-[10px] tracking-wider">Total</span>
                      <span className="text-violet-100 font-black meter-display" data-testid="group-quote-total">${Number(gq.subtotal).toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="text-[9px] text-zinc-500 mt-1.5">Auto-priced from the group rate table — {paxN} × ${effPp.toFixed(2)}/rider{isRT ? " (incl. round trip)" : ""}{bookingFee > 0 ? ` + $${bookingFee.toFixed(2)} booking fee` : ""}. Editing the total won&apos;t change this reference.</div>
                </div>
              );
            })()}

            {/* Round trip */}
            <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-2.5">
              <label className="flex items-center justify-between gap-2 cursor-pointer" data-testid="dispatch-round-trip-toggle">
                <span className="text-[10px] uppercase tracking-widest text-yellow-200 font-bold flex items-center gap-1">
                  <MapPinned className="h-3 w-3 text-yellow-300"/>Round Trip
                </span>
                <input type="checkbox" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} className="h-4 w-4 rounded accent-yellow-400" data-testid="dispatch-round-trip-checkbox"/>
              </label>
              {roundTrip && (
                <div className="mt-2.5 space-y-2" data-testid="dispatch-return-fields">
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-zinc-200">
                    <input type="checkbox" checked={returnReverse} onChange={(e) => setReturnReverse(e.target.checked)} className="h-4 w-4 rounded accent-yellow-400" data-testid="dispatch-return-reverse"/>
                    Return route is the reverse (Drop-off → Pickup)
                  </label>
                  {returnReverse ? (
                    <div className="text-[11px] text-zinc-400 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5" data-testid="dispatch-return-reverse-preview">
                      Return: <span className="text-zinc-200">{destination || "drop-off"}</span> → <span className="text-zinc-200">{origin || "pickup"}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <AddressAutocomplete value={returnPickup} onChange={setReturnPickup} placeholder="Return pickup address" testid="dispatch-return-pickup"/>
                      <AddressAutocomplete value={returnDropoff} onChange={setReturnDropoff} placeholder="Return drop-off address" testid="dispatch-return-dropoff"/>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9" data-testid="dispatch-return-date"/>
                    <Input type="time" value={returnTime} onChange={(e) => setReturnTime(e.target.value)} className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9" data-testid="dispatch-return-time"/>
                  </div>
                </div>
              )}
            </div>

            {/* PROMPT 1 · Language flag — identify riders who don't speak English.
                The choice is auto-appended to the driver-visible notes below. */}
            <div data-testid="language-flag-section">
              <label className="text-[10px] uppercase tracking-widest text-yellow-200 font-bold flex items-center gap-1 mb-1">
                <Languages className="h-3 w-3 text-yellow-300"/>Language <span className="text-zinc-500 normal-case tracking-normal">(optional — shown to the driver)</span>
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { key: "", label: "English" },
                  { key: "Spanish", label: "Spanish" },
                  { key: "Asian", label: "Asian" },
                  { key: "Other", label: "Other" },
                ].map((opt) => {
                  const on = language === opt.key;
                  return (
                    <button
                      type="button"
                      key={opt.key || "english"}
                      onClick={() => { setLanguage(opt.key); if (opt.key !== "Other") setLanguageOther(""); }}
                      aria-pressed={on}
                      data-testid={`language-flag-${opt.key ? opt.key.toLowerCase() : "english"}`}
                      className={`rounded-md border px-2 py-1.5 text-[12px] font-bold transition-colors ${on ? "border-yellow-400 bg-yellow-400/15 text-yellow-100" : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {language === "Other" && (
                <Input
                  value={languageOther}
                  onChange={(e) => setLanguageOther(e.target.value)}
                  placeholder="Specify language (e.g. Vietnamese, Arabic)…"
                  className="mt-1.5 bg-zinc-900 border-zinc-700 text-zinc-100 h-9 text-sm"
                  data-testid="language-flag-other"
                />
              )}
            </div>

            {/* Dispatcher notes — an auto-populated trip summary (read-only) plus a
                manual area. Both are combined into the single driver-visible note. */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-yellow-200 font-bold flex items-center gap-1 mb-1">
                <Info className="h-3 w-3 text-yellow-300"/>Dispatcher Notes <span className="text-zinc-500 normal-case tracking-normal">(visible to the driver)</span>
              </label>
              {autoNotes && (
                <pre
                  data-testid="dispatch-notes-auto"
                  className="w-full whitespace-pre-wrap break-words bg-zinc-950 border border-yellow-500/30 rounded px-3 py-2 text-[11px] leading-relaxed text-yellow-100/90 font-sans mb-1.5"
                >{autoNotes}</pre>
              )}
              <textarea
                value={dispatcherNotes}
                onChange={(e) => setDispatcherNotes(e.target.value)}
                placeholder="Additional notes — gate codes, rider details, special instructions…"
                rows={2}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 resize-y"
                data-testid="dispatch-notes"
              />
              <div className="text-[9px] text-zinc-500 mt-0.5" data-testid="dispatch-notes-hint">
                The trip summary above is auto-added for the driver. Type any extra instructions here.
              </div>
            </div>

            {/* v2.8+E328 · Booking type — tags the trip source at creation:
                a rider who called in vs a dispatch-initiated trip. */}
            <div data-testid="booking-type-section">
              <label className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1 mb-1">
                Booking Type <span className="text-zinc-500 normal-case tracking-normal">(trip source)</span>
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { key: "call_in", label: "Rider Called In", desc: "A rider phoned dispatch" },
                  { key: "self", label: "Dispatch Initiated", desc: "Dispatch created this trip" },
                ].map((opt) => {
                  const on = dispatchOrigin === opt.key;
                  return (
                    <button
                      type="button"
                      key={opt.key}
                      onClick={() => setDispatchOrigin(opt.key)}
                      aria-pressed={on}
                      data-testid={`booking-type-${opt.key}`}
                      className={`rounded-md border px-2.5 py-2 text-left transition-colors ${on ? "border-amber-400 bg-amber-400/15 text-amber-100" : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"}`}
                    >
                      <div className="text-[12px] font-bold">{opt.label}</div>
                      <div className="text-[9px] text-zinc-500">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* v2.8+E319 · Rider Flags — dispatcher-only safety flags that
                persist to the ride card + driver assignment. */}
            <div data-testid="rider-flags-section">
              <label className="text-[10px] uppercase tracking-widest text-red-300 font-bold flex items-center gap-1 mb-1">
                <Flag className="h-3 w-3 text-red-400"/>Rider Flags <span className="text-zinc-500 normal-case tracking-normal">(optional — alerts dispatch &amp; driver)</span>
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                {RIDER_FLAGS.map((f) => {
                  const on = riderFlags.includes(f);
                  return (
                    <button
                      type="button"
                      key={f}
                      onClick={() => setRiderFlags((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f])}
                      aria-pressed={on}
                      data-testid={`rider-flag-${f.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-semibold text-left transition-colors ${on ? (isCriticalFlag(f) ? "border-red-400 bg-red-600/30 text-white font-bold" : "border-red-500 bg-red-500/20 text-red-200") : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"}`}
                    >
                      <span className={`h-3.5 w-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 ${on ? "bg-red-500 border-red-500" : "border-zinc-600"}`}>
                        {on && <Check className="h-2.5 w-2.5 text-white"/>}
                      </span>
                      {f}
                    </button>
                  );
                })}
              </div>
              {riderFlags.length > 0 && (
                <div className="text-[9px] text-red-300 mt-1 font-bold" data-testid="rider-flags-count">
                  {riderFlags.length} flag{riderFlags.length > 1 ? "s" : ""} selected — the driver will be alerted before pickup.
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
            <CalendarClock className="h-3 w-3 text-amber-400"/>Would you like to book this trip in advance?
          </label>
          <select
            value={scheduleMode}
            onChange={(e) => setScheduleMode(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100"
            data-testid="hero-schedule-mode"
          >
            <option value="asap">No — Send ASAP</option>
            <option value="scheduled">Yes — Pick a date &amp; time</option>
          </select>
        </div>
        {scheduleMode === "scheduled" && (
          <div className="grid grid-cols-2 gap-2.5" data-testid="hero-schedule-fields">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
                <CalendarClock className="h-3 w-3 text-amber-400"/>Pickup date
              </label>
              <Input
                type="date" value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9"
                data-testid="hero-schedule-date"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1 mb-1">
                <Clock className="h-3 w-3 text-amber-400"/>Pickup time
              </label>
              <Input
                type="time" value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9"
                data-testid="hero-schedule-time"
              />
            </div>
          </div>
        )}

        {/* v2.7.65+E215 · Dispatcher-only Book-Ahead → Future Fares. Additive;
            hidden on the public site and for voucher trips. Routes a 24h+
            scheduled ride into the Future Fares calendar. */}
        {dispatcherMode && scheduleMode === "scheduled" && !isVoucherTrip && (
          <label
            className="flex items-start gap-2 rounded-md border border-violet-400/40 bg-violet-500/10 px-3 py-2 cursor-pointer"
            data-testid="hero-book-ahead-wrap"
          >
            <input
              type="checkbox"
              checked={bookAhead}
              onChange={(e) => { setBookAhead(e.target.checked); setBookAheadTouched(true); }}
              data-testid="hero-book-ahead"
              className="mt-0.5 accent-violet-500"
            />
            <span className="text-[11px] text-violet-100">
              <span className="font-bold flex items-center gap-1">
                <CalendarClock className="h-3 w-3"/>Book-Ahead → Future Fares
                {bookAhead && !bookAheadTouched && (
                  <span className="ml-1 rounded-full bg-violet-500/30 border border-violet-400/50 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-violet-100 font-bold" data-testid="hero-book-ahead-auto">
                    Auto-selected · 24h+ advance
                  </span>
                )}
              </span>
              <span className="block text-[10px] text-zinc-400 mt-0.5">Sends this advance booking to the Future Fares calendar instead of the live queue. Applies when pickup is 24h+ away; under 24h uses the standard flow automatically.</span>
            </span>
          </label>
        )}

        {/* v2.7.65+E132 · Community Voucher Program integration. When "Yes" is
            selected we enforce (client + server) a 24-72h lead time AND a
            Weatherford city-limits geofence on pickup + drop-off. The dispatch
            console tags the booking with the serial so the driver verifies
            the physical voucher at pickup. */}
        <div data-testid="hero-voucher-block" className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/5 p-3 space-y-2">
          <label className="text-[10px] uppercase tracking-widest text-fuchsia-200 font-bold flex items-center gap-1">
            <Ticket className="h-3 w-3 text-fuchsia-300"/>Are you a CCVAP Program (Community Care Voucher Assistance Program) Recipient?
          </label>
          <select
            value={isVoucherTrip ? "yes" : "no"}
            onChange={(e) => {
              const yes = e.target.value === "yes";
              setIsVoucherTrip(yes);
              if (yes) {
                // Voucher trips must be scheduled — flip the mode automatically.
                setScheduleMode("scheduled");
              } else {
                setVoucherSerial("");
                setVoucherStatus(null);
              }
            }}
            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100"
            data-testid="hero-voucher-toggle"
          >
            <option value="no">No</option>
            <option value="yes">Yes — I have a CCVAP voucher</option>
          </select>
          {isVoucherTrip && (
            <div className="space-y-2" data-testid="hero-voucher-fields">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-fuchsia-200 font-bold flex items-center gap-1 mb-1">
                  <Ticket className="h-3 w-3 text-fuchsia-300"/>Voucher Serial Number
                </label>
                <Input
                  value={voucherSerial}
                  onChange={(e) => setVoucherSerial(e.target.value)}
                  placeholder="e.g. BT-VCH-000123"
                  className="bg-zinc-900 border-fuchsia-400/50 text-zinc-100 h-9"
                  data-testid="hero-voucher-serial"
                />
                {voucherValidating && (
                  <div className="mt-1 text-[10px] text-zinc-400 flex items-center gap-1" data-testid="hero-voucher-checking">
                    <Loader2 className="h-3 w-3 animate-spin"/>Checking serial…
                  </div>
                )}
                {!voucherValidating && voucherStatus?.ok === true && (
                  <div className="mt-1 text-[10px] text-emerald-300 flex items-center gap-1 bg-emerald-500/10 border border-emerald-400/30 rounded px-2 py-1" data-testid="hero-voucher-valid">
                    <CheckCircle2 className="h-3 w-3"/>Voucher recognized{voucherStatus.rider_first_name ? ` · ${voucherStatus.rider_first_name}` : ""}
                  </div>
                )}
                {!voucherValidating && voucherStatus?.ok === false && (
                  <div className="mt-1 text-[10px] text-rose-300 flex items-center gap-1 bg-rose-500/10 border border-rose-400/30 rounded px-2 py-1" data-testid="hero-voucher-invalid">
                    <X className="h-3 w-3"/>{voucherStatus.error}
                  </div>
                )}
              </div>
              <div className="text-[10px] text-fuchsia-100 bg-fuchsia-500/10 border border-fuchsia-400/25 rounded px-2 py-1.5 flex items-start gap-1.5" data-testid="hero-voucher-rules">
                <Info className="h-3 w-3 text-fuchsia-300 flex-shrink-0 mt-0.5"/>
                <span>
                  <span className="font-bold">CCVAP Program policy:</span> voucher trips must be scheduled between <span className="font-bold">24 and 72 hours</span> in advance, and both pickup and drop-off must be inside <span className="font-bold">Weatherford city limits</span>.
                </span>
              </div>
              <div className="text-[10px] text-zinc-400 bg-zinc-900/70 border border-zinc-800 rounded px-2 py-1.5 flex items-start gap-1.5" data-testid="hero-voucher-city-hint">
                <MapPinned className="h-3 w-3 text-fuchsia-300 flex-shrink-0 mt-0.5"/>
                <span>CCVAP vouchers are valid <span className="font-bold text-fuchsia-200">exclusively</span> for local travel within Weatherford, OK.</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* v2.7.0 batch DH · Dynamic surcharge banner (only shows when an active
          surcharge applies to the rider's chosen pickup time / today). */}
      {surcharges && (surcharges.late_night?.active || surcharges.holiday?.active) && (
        <div
          className="mt-3 rounded-lg border-2 border-amber-400/60 bg-amber-400/10 px-3 py-2.5 space-y-1.5"
          data-testid="hero-surcharge-banner"
        >
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-extrabold flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5"/>Surcharge in effect for your pickup time
          </div>
          {surcharges.late_night?.active && (
            <div className="flex items-start gap-2 text-xs text-amber-100" data-testid="hero-surcharge-late-night">
              <Moon className="h-3.5 w-3.5 text-amber-300 flex-shrink-0 mt-0.5"/>
              <span>
                <span className="font-bold">{surcharges.late_night.label}</span>{" "}
                — additional <span className="font-bold">+{surcharges.late_night.pct}%</span> applied for pickups
                between {formatHourMin(surcharges.late_night.start_hour, surcharges.late_night.start_minute)} and {formatHourMin(surcharges.late_night.end_hour, surcharges.late_night.end_minute)}.
              </span>
            </div>
          )}
          {surcharges.holiday?.active && (
            <div className="flex items-start gap-2 text-xs text-amber-100" data-testid="hero-surcharge-holiday">
              <PartyPopper className="h-3.5 w-3.5 text-amber-300 flex-shrink-0 mt-0.5"/>
              <span>
                <span className="font-bold">{surcharges.holiday.label}</span>{" "}
                — additional <span className="font-bold">+{surcharges.holiday.pct}%</span> applied because your pickup falls on a recognized holiday
                {surcharges.holiday.matched_date ? ` (${surcharges.holiday.matched_date})` : ""}.
              </span>
            </div>
          )}
        </div>
      )}

      {/* QUOTE PANEL — Updated for batch DE: now shows "Pay & Book" instead of
          old "Open Booking" green button (which required signup). Card form
          slides in below when rider chooses the card path. */}
      {quote && (
        <div className="mt-3 rounded-lg border-2 border-amber-400/50 p-3 bg-black" data-testid="hero-quote-result">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Estimated Fare</div>
              <div className="meter-display text-3xl font-extrabold leading-none mt-1" style={{ color: "#FFD400" }} data-testid="hero-quote-total">
                {fmtUsd(quote.total)}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                {(quote.miles || 0).toFixed(1)} mi · {Math.round(quote.minutes || 0)} min
                {quote.stop_count > 0 ? ` · ${quote.stop_count} stop${quote.stop_count === 1 ? "" : "s"}` : ""}
              </div>
              {/* Fare detail chips — only render when an applicable line item is present */}
              {(quote.stop_count_fee > 0 || quote.late_night > 0 || quote.holiday > 0) && (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="hero-quote-detail-chips">
                  {quote.stop_count_fee > 0 && (
                    <span className="text-[10px] bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-300" data-testid="hero-chip-stops">
                      +{fmtUsd(quote.stop_count_fee)} stops
                    </span>
                  )}
                  {quote.late_night > 0 && (
                    <span className="text-[10px] bg-zinc-900 border border-amber-400/40 rounded px-1.5 py-0.5 text-amber-200" data-testid="hero-chip-late-night">
                      +{fmtUsd(quote.late_night)} late night
                    </span>
                  )}
                  {quote.holiday > 0 && (
                    <span className="text-[10px] bg-zinc-900 border border-amber-400/40 rounded px-1.5 py-0.5 text-amber-200" data-testid="hero-chip-holiday">
                      +{fmtUsd(quote.holiday)} holiday
                    </span>
                  )}
                </div>
              )}
            </div>
            {/* v2.7.58+E41 · Credit-card up-front hold path is TEMPORARILY
                DISABLED per operator request. The underlying Square token
                pipeline (onCardTokenized / showCardForm / authorizing) is
                intentionally left in the source so it can be re-enabled by
                un-commenting the button + inline form below. For now
                riders pay their driver at the end of the trip. */}
          </div>
        </div>
      )}

      {/* v2.7.58+E41 · "Pay at pickup" reminder + digital-wallet panel.
          Renders whenever the rider hasn't submitted yet so they see how
          card + Cash App + Venmo will play out at drop-off. */}
      {!confirmation && !dispatcherMode && (
        <div className="mt-3 rounded-lg border border-emerald-400/40 bg-emerald-500/5 p-3" data-testid="hero-payment-notice">
          <div className="flex items-start gap-2">
            <CreditCard className="h-4 w-4 text-emerald-300 flex-shrink-0 mt-0.5"/>
            <div className="text-[11px] text-emerald-100 leading-snug">
              <span className="font-bold">Paying by card?</span> Simply provide your card to your driver at the end of your trip, and they will securely process the payment for you.
            </div>
          </div>
          {payQrs && (payQrs.cashapp_qr_url || payQrs.venmo_qr_url) && (
            <div className="mt-3 pt-3 border-t border-emerald-400/20" data-testid="hero-payment-qrs">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold flex items-center gap-1 mb-2">
                <QrCode className="h-3 w-3"/>Or scan to pay us direct
              </div>
              <div className="grid grid-cols-2 gap-2">
                {payQrs.cashapp_qr_url && (
                  <div className="bg-zinc-950 border border-zinc-800 rounded p-2 flex flex-col items-center gap-1.5" data-testid="hero-cashapp-panel">
                    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Cash App</div>
                    <img
                      src={payQrs.cashapp_qr_url}
                      alt="Cash App QR"
                      className="w-24 h-24 object-contain bg-white rounded p-1"
                      data-testid="hero-cashapp-qr"
                    />
                    {payQrs.cashapp_handle && (
                      <div className="text-[10px] font-mono text-emerald-300" data-testid="hero-cashapp-handle">{payQrs.cashapp_handle}</div>
                    )}
                  </div>
                )}
                {payQrs.venmo_qr_url && (
                  <div className="bg-zinc-950 border border-zinc-800 rounded p-2 flex flex-col items-center gap-1.5" data-testid="hero-venmo-panel">
                    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Venmo</div>
                    <img
                      src={payQrs.venmo_qr_url}
                      alt="Venmo QR"
                      className="w-24 h-24 object-contain bg-white rounded p-1"
                      data-testid="hero-venmo-qr"
                    />
                    {payQrs.venmo_handle && (
                      <div className="text-[10px] font-mono text-emerald-300" data-testid="hero-venmo-handle">{payQrs.venmo_handle}</div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-center gap-1 text-[9px] text-zinc-500">
                <Banknote className="h-2.5 w-2.5"/>Cash accepted too — just hand it to your driver.
              </div>
            </div>
          )}
        </div>
      )}

      {dispatcherMode && dupWarning && (
        <div className="mt-3 rounded-lg border-2 border-amber-400/60 bg-amber-400/10 p-3" data-testid="dispatch-duplicate-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-300 flex-shrink-0 mt-0.5"/>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-amber-300 font-black">Possible duplicate booking</div>
              <div className="text-xs text-zinc-200 mt-1">
                <span className="font-bold">{dupWarning.customer_name || "This caller"}</span> was just booked for this same route in the last 10 minutes
                {dupWarning.status ? <span className="text-zinc-400"> · {dupWarning.status}</span> : null}.
              </div>
              <div className="text-[11px] text-zinc-400 mt-0.5 truncate">
                {dupWarning.pickup} → {dupWarning.dropoff}{dupWarning.trip_id ? <span className="text-zinc-600"> · {dupWarning.trip_id}</span> : null}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">Double-check before booking so the same ride doesn&apos;t go out twice.</div>
            </div>
            <button
              type="button"
              onClick={() => { setDupDismissedKey(dupWarning.key); setDupWarning(null); }}
              data-testid="dispatch-duplicate-dismiss"
              className="text-[10px] font-bold text-amber-300 hover:text-amber-200 border border-amber-500/40 rounded px-2 py-1 flex-shrink-0"
            >
              Book anyway
            </button>
          </div>
        </div>
      )}

      <div className={`grid ${dispatcherMode ? "grid-cols-1" : "grid-cols-2"} gap-2 mt-3`}>
        {!dispatcherMode && (
          <Button
            data-testid="hero-quote-btn"
            onClick={onQuote}
            disabled={quoting || submitting || authorizing}
            variant="outline"
            className="bg-zinc-950 border-amber-400/60 text-amber-300 hover:bg-amber-400/10 font-extrabold tracking-wider uppercase"
          >
            {quoting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>…</> : <><Calculator className="h-4 w-4 mr-1.5"/>Quote</>}
          </Button>
        )}
        <Button
          data-testid="hero-request-dispatch"
          onClick={onRequestDispatch}
          disabled={submitting || quoting || authorizing}
          className={`${dispatcherMode ? "bg-yellow-400 hover:bg-yellow-300" : "bg-amber-400 hover:bg-amber-300"} text-zinc-950 font-extrabold tracking-wider uppercase`}
        >
          {submitting
            ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>{dispatcherMode ? "Booking…" : "Sending…"}</>
            : dispatcherMode
              ? <><Car className="h-4 w-4 mr-1.5"/>Book Now</>
              : <><Send className="h-4 w-4 mr-1.5"/>Pay at Pickup</>}
        </Button>
      </div>
      {/* v2.7.65+E165 · SMS consent — recorded server-side with the booking.
          Hidden in dispatcher mode (staff-entered phone bookings). */}
      {!dispatcherMode && (
        <SmsConsent
          value={smsConsent}
          onChange={setSmsConsent}
          testIdPrefix="qb-sms-consent"
          className="mt-3 pt-3 border-t border-zinc-800"
        />
      )}
      {!dispatcherMode && (
        <div className="mt-1.5 text-[9px] text-zinc-500 text-center leading-tight">
          <Calculator className="h-2.5 w-2.5 inline-block mr-0.5"/>Hit <span className="text-amber-300 font-bold">QUOTE</span> to see the fare estimate,
          then <span className="text-amber-300 font-bold">PAY AT PICKUP</span> to dispatch — settle with the driver in cash, card, Cash App, or Venmo.
        </div>
      )}
    </Card>
  );
}
